import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { getProjectDetailsRequest } from '../store/actions/projectActions'
import { projectService } from '../services/projectService'
import { coreService } from '../services/coreService'
import { requirementService } from '../services/requirementService'
import { userStoryService, UserStory } from '../services/userStoryService'
import { testCaseService } from '../services/testCaseService'
import { productIntelligenceService, ProductIntelligenceData } from '../services/productIntelligenceService'
import { Button } from '../components/ui/Button'
import { Slider } from '../components/ui/Slider'
import { Switch } from '../components/ui/Switch'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { DownloadConfirmationModal } from '../components/ui/DownloadConfirmationModal'
import { EstimationCalculator } from '../components/EstimationCalculator'
import { LoadingOverlay } from '../components/ui/LoadingOverlay'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import JSZip from 'jszip'
import toast from 'react-hot-toast'
import { ArrowDownTrayIcon, PencilSquareIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { generateTestCasesBulkPDF } from '../utils/testCasePDFGenerator'
import {
    HomeIcon,
    PlusIcon,
    ChevronRightIcon,
    BriefcaseIcon,
    QueueListIcon,
    ShieldCheckIcon,
    WindowIcon,
    PresentationChartBarIcon,
    CommandLineIcon,
    BeakerIcon,
    LinkIcon,
    IdentificationIcon,
    LightBulbIcon,
    DocumentTextIcon,
    TrashIcon,
    XMarkIcon,
    SparklesIcon,
    ListBulletIcon,
    InformationCircleIcon,
    CalculatorIcon,
    MagnifyingGlassIcon,
    ClipboardDocumentCheckIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowRightIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    BoltIcon,
    ChartPieIcon,
    CpuChipIcon,
    CalendarDaysIcon,
    ClockIcon,
    CircleStackIcon,
    ChartBarIcon,
    LockClosedIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { authService } from '../services/authService'
import { IntegrationsPage } from './IntegrationsPage'
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MermaidDiagram from '../components/MermaidDiagram';

// Helper for saving with File System Access API
const saveFileWithPicker = async (blob: Blob, suggestedName: string, mimeType: string, extension: string) => {
    const toastId = toast.loading(`Preparing ${extension.toUpperCase()}...`);
    try {
        // @ts-ignore
        if (window.showSaveFilePicker) {
            try {
                // @ts-ignore
                const handle = await window.showSaveFilePicker({
                    suggestedName,
                    types: [{
                        description: extension.toUpperCase() === '.ZIP' ? 'ZIP Archive' : 'PDF Document',
                        accept: { [mimeType]: [extension] },
                    }],
                });
                const writable = await handle.createWritable();
                await writable.write(blob);
                await writable.close();
                toast.success(`Saved successfully`, { id: toastId });
                return;
            } catch (err: any) {
                if (err.name === 'AbortError') {
                    toast.dismiss(toastId);
                    return;
                }
                console.warn('File System Picker failed, falling back to default download', err);
            }
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = suggestedName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        toast.success('Download started', { id: toastId });
    } catch (err: any) {
        console.error('Save Error:', err);
        toast.error('Failed to save file', { id: toastId });
    }
};

const stripHtml = (html: string) => {
    if (!html) return '';
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let text = doc.body.textContent || '';
        // Replace non-breaking hyphens and en/em dashes with standard hyphen
        text = text.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\u00AD]/g, '-');
        // Replace non-breaking spaces and other unicode spaces with a standard space
        text = text.replace(/[\u00A0\u2007\u202F\u205F\u3000]/g, ' ');
        // Normalize carriage returns and double/multiple spaces
        text = text.replace(/\r/g, '').replace(/\s+/g, ' ');
        return text.trim();
    } catch (e) {
        let text = html.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\u00AD]/g, '-');
        text = text.replace(/[\u00A0\u2007\u202F\u205F\u3000]/g, ' ').replace(/\r/g, '').replace(/\s+/g, ' ');
        return text.trim();
    }
};


interface Requirement {
    id: string
    title: string
    type: string
    status: string
    description?: string
    screens?: string
    personas?: string
    parentId?: string | null
    linkedIds?: string[] | null
    parent?: Requirement | null
    children?: Requirement[]
    isVerified?: boolean
}

interface Project {
    id: string
    name: string
    description?: string
    status: string
    technicalDetails?: string
    projectType?: 'TECH' | 'NON_TECH'
    requirements?: Requirement[]
    businessProcess?: {
        mermaidCode: string
        generatedAt: string
    } | null
    estimation?: any | null
    estimationConfig?: any | null
    preferences?: any
}

export const ProjectDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { token, user } = useSelector((state: RootState) => state.auth)
    const dispatch = useDispatch()
    const { activeProject: project, isLoading } = useSelector((state: RootState) => state.projects)

    const location = useLocation()
    const [isEditingCore, setIsEditingCore] = useState(false)
    const [editDesc, setEditDesc] = useState('')
    const [editTech, setEditTech] = useState('')
    const [preferences, setPreferences] = useState<any>({
        brd: { enabled: true, value: 15 },
        prd: { enabled: true, value: 15 },
        uir: { enabled: true, value: 15 },
        nfr: { enabled: true, value: 15 },
    })
    const [isRegenerating, setIsRegenerating] = useState(false)

    const updatePreference = (key: 'brd' | 'prd' | 'uir' | 'nfr', field: 'enabled' | 'value', value: any) => {
        setPreferences((prev: any) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value
            }
        }))
    }

    useEffect(() => {
        if (project) {
            setEditDesc(project.description || '')
            setEditTech(project.technicalDetails || '')
            if (project.preferences) {
                setPreferences({
                    brd: project.preferences.brd || { enabled: true, value: 15 },
                    prd: project.preferences.prd || { enabled: true, value: 15 },
                    uir: project.preferences.uir || { enabled: true, value: 15 },
                    nfr: project.preferences.nfr || { enabled: true, value: 15 },
                })
            }
        }
    }, [project])

    const reqCounts = {
        BRD: project?.requirements?.filter(r => r.type === 'BRD').length || 0,
        PRD: project?.requirements?.filter(r => r.type === 'PRD').length || 0,
        NFR: project?.requirements?.filter(r => r.type === 'NFR').length || 0,
        UIR: project?.requirements?.filter(r => r.type === 'UIR').length || 0,
        BP: project?.requirements?.filter(r => r.type === 'BP').length || 0,
    }

    const verifiedStatuses = {
        BRD: reqCounts.BRD > 0 && project?.requirements?.filter(r => r.type === 'BRD').every(r => r.isVerified),
        PRD: reqCounts.PRD > 0 && project?.requirements?.filter(r => r.type === 'PRD').every(r => r.isVerified),
        NFR: reqCounts.NFR > 0 && project?.requirements?.filter(r => r.type === 'NFR').every(r => r.isVerified),
        UIR: reqCounts.UIR > 0 && project?.requirements?.filter(r => r.type === 'UIR').every(r => r.isVerified),
        BP: reqCounts.BP > 0 && project?.requirements?.filter(r => r.type === 'BP').every(r => r.isVerified),
    }

    const isNonTech = project?.projectType === 'NON_TECH'

    // Business Process, Market Intelligence, and Estimation Calculator are unlocked
    // only when BRD, PRD, NFR, and UIR are all completed (have items) AND fully verified.
    // For Non-Tech projects, Market Intelligence is unlocked directly without requirement verification.
    const prerequisitesMet = isNonTech ? true : !!(
        verifiedStatuses.BRD &&
        verifiedStatuses.PRD &&
        verifiedStatuses.NFR &&
        verifiedStatuses.UIR
    )

    useEffect(() => {
        if (location.state?.editCore && !prerequisitesMet) {
            setIsEditingCore(true)
        }
    }, [location.state, prerequisitesMet])

    const handleSaveAndRegenerate = async () => {
        if (!project || !id) return

        const loadingToast = toast.loading('Saving project core and regenerating requirements...')
        setIsRegenerating(true)
        try {
            // 1. Save updated details to project
            await projectService.updateProject(id, {
                description: editDesc,
                technicalDetails: editTech,
                preferences: preferences
            })

            // 2. Call generate all requirements API
            const counts = {
                BRD: preferences.brd.enabled ? preferences.brd.value : 0,
                PRD: preferences.prd.enabled ? preferences.prd.value : 0,
                UIR: preferences.uir.enabled ? preferences.uir.value : 0,
                NFR: preferences.nfr.enabled ? preferences.nfr.value : 0,
            }

            await projectService.generateAllRequirements(id, {
                description: editDesc,
                technical: editTech,
                counts
            })

            toast.dismiss(loadingToast)
            toast.success('Project updated and all requirements regenerated successfully!')
            setIsEditingCore(false)

            // Reload project details
            dispatch(getProjectDetailsRequest(id))
        } catch (error: any) {
            console.error('Failed to regenerate project details:', error)
            toast.dismiss(loadingToast)
            toast.error(error.response?.data?.message || 'Failed to regenerate requirements.')
        } finally {
            setIsRegenerating(false)
        }
    }

    // Check subscribed status (S = Subscribed/Premier)
    const isPro = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise'

    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab = (searchParams.get('tab') as any) || 'project'
    const [selectedReqId, setSelectedReqId] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [isFlowView, setIsFlowView] = useState(false)
    const [flowSteps, setFlowSteps] = useState<any[]>([])
    const [flowVersion, setFlowVersion] = useState(0)
    const [flowTheme, setFlowTheme] = useState<'indigo' | 'purple' | 'emerald' | 'orange' | 'cyan' | 'rose'>('indigo')
    const [savedFlows, setSavedFlows] = useState<Record<string, { steps: any[], theme: any }>>({})
    const flowRef = React.useRef<HTMLDivElement>(null)
    const [rightSidebarTab, setRightSidebarTab] = useState<'chat' | 'link'>('link')
    const [rightSidebarTabSub, setRightSidebarTabSub] = useState<'BRD' | 'PRD'>('PRD')
    const [requirementLinks, setRequirementLinks] = useState<Record<string, string[]>>({
        'PRD-02': ['BRD-03']
    })

    const [editTitle, setEditTitle] = useState('')
    const [editDescription, setEditDescription] = useState('')
    const [editParentId, setEditParentId] = useState<string | null>(null)
    const [selectedLinkedIds, setSelectedLinkedIds] = useState<string[]>([])
    const [isLinking, setIsLinking] = useState(false)
    const [linkSearchTerm, setLinkSearchTerm] = useState('')
    const [isEnhancing, setIsEnhancing] = useState(false)
    const [isGeneratingBp, setIsGeneratingBp] = useState(false)
    const [isEditingBp, setIsEditingBp] = useState(false)
    const [bpCode, setBpCode] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isGenModalOpen, setIsGenModalOpen] = useState(false)
    const [genType, setGenType] = useState<'BRD' | 'PRD' | 'NFR' | 'UIR' | 'BP'>('BRD')
    const [genCount, setGenCount] = useState(15)
    const [isRegenerateMode, setIsRegenerateMode] = useState(false)
    const [userThoughts, setUserThoughts] = useState('')
    const [isGeneratingBatch, setIsGeneratingBatch] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [selectedUserStories, setSelectedUserStories] = useState<UserStory[]>([])
    const [isLoadingStories, setIsLoadingStories] = useState(false)
    const [testCaseViewPrdId, setTestCaseViewPrdId] = useState<string | null>(null)
    const [storiesWithTestCounts, setStoriesWithTestCounts] = useState<{ story: UserStory, count: number }[]>([])

    const [tokenUsage, setTokenUsage] = useState<any>(null)
    const [tokenHistory, setTokenHistory] = useState<any[]>([])
    const [isLoadingTokenData, setIsLoadingTokenData] = useState(false)

    // Market Intelligence states
    const [intelligenceData, setIntelligenceData] = useState<ProductIntelligenceData | null>(null)
    const [isLoadingIntelligence, setIsLoadingIntelligence] = useState(false)
    const [isPollingIntelligence, setIsPollingIntelligence] = useState(false)
    const [showDeepScanMessage, setShowDeepScanMessage] = useState(false)
    const pollCountRef = useRef(0)
    const [piSubTab, setPiSubTab] = useState<'overview' | 'market' | 'swot' | 'competitors' | 'pitchdeck'>('overview')

    // Confirmation Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingDownload, setPendingDownload] = useState<(() => void) | null>(null);

    const allNavigationItems = [
        { id: 'project' as const, name: 'Project', icon: LightBulbIcon, count: null },
        { id: 'brd' as const, name: 'Business Requirements', icon: BriefcaseIcon, count: reqCounts.BRD, type: 'BRD' as const, isTechOnly: true },
        { id: 'prd' as const, name: 'Product Requirements', icon: QueueListIcon, count: reqCounts.PRD, type: 'PRD' as const, isTechOnly: true },
        { id: 'nfr' as const, name: 'Non Functional Requirements', icon: ShieldCheckIcon, count: reqCounts.NFR, type: 'NFR' as const, isTechOnly: true },
        { id: 'uir' as const, name: 'User Interface Requirements', icon: WindowIcon, count: reqCounts.UIR, type: 'UIR' as const, isTechOnly: true },
        { id: 'bp' as const, name: 'Business Process', icon: CommandLineIcon, count: reqCounts.BP, type: 'BP' as const, isLocked: !prerequisitesMet, isTechOnly: true },
        { id: 'estimation' as const, name: 'Estimation Calculator', icon: CalculatorIcon, count: null, isLocked: !prerequisitesMet, isTechOnly: true },
        { id: 'product-intelligence' as const, name: 'Market Intelligence', icon: PresentationChartBarIcon, count: null, isLocked: !prerequisitesMet },
    ]

    const navigationItems = isNonTech
        ? allNavigationItems.filter(item => !item.isTechOnly)
        : allNavigationItems

    const isTabVerified = verifiedStatuses[activeTab.toUpperCase() as keyof typeof verifiedStatuses] || false;

    const handleToggleVerifyType = async () => {
        if (!project || !id) return;
        const type = activeTab.toUpperCase();
        if (!['BRD', 'PRD', 'NFR', 'UIR', 'BP'].includes(type)) return;

        const tabReqs = project.requirements?.filter(r => r.type === type) || [];
        if (tabReqs.length === 0) return;

        const shouldVerify = !tabReqs.every(r => r.isVerified);

        const loadingToast = toast.loading(`${shouldVerify ? 'Verifying' : 'Unverifying'} ${type} requirements...`);

        try {
            await requirementService.bulkVerifyRequirements(id, type, shouldVerify);

            // Refresh project after verification
            dispatch(getProjectDetailsRequest(id));

            toast.dismiss(loadingToast);
            toast.success(`${type} requirements ${shouldVerify ? 'verified' : 'unverified'} successfully!`);
        } catch (error) {
            console.error('Failed to update verification status:', error);
            toast.dismiss(loadingToast);
            toast.error('Failed to update verification status');
        }
    };

    const triggerDownload = (downloadFn: () => void) => {
        if (isPro) {
            downloadFn();
        } else {
            setPendingDownload(() => downloadFn);
            setShowConfirmModal(true);
        }
    };

    const handleConfirmDownload = () => {
        if (pendingDownload) {
            pendingDownload();
            setPendingDownload(null);
        }
        setShowConfirmModal(false);
    };

    const generatePDFBlob = async (title: string, requirements: Requirement[], pName: string) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // 1. Cover Page
        pdf.setFillColor(15, 23, 42); // Navy background
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

        pdf.setTextColor(249, 115, 22); // Orange accent
        pdf.setFontSize(36);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REQUIREMENTS DOCUMENT', pdfWidth / 2, 100, { align: 'center' });

        pdf.setDrawColor(249, 115, 22);
        pdf.setLineWidth(1);
        pdf.line(pdfWidth / 2 - 40, 115, pdfWidth / 2 + 40, 115);

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(20);
        pdf.text(pName.toUpperCase(), pdfWidth / 2, 135, { align: 'center' });

        pdf.setFontSize(14);
        pdf.text(title.toUpperCase(), pdfWidth / 2, 148, { align: 'center' });

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(200, 200, 200);
        pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pdfWidth / 2, 165, { align: 'center' });
        pdf.text('DocSprintAI Architecture Platform', pdfWidth / 2, 172, { align: 'center' });

        // 2. Content Pages
        pdf.addPage();
        let yPos = 30;
        const margin = 20;

        const addHeaderFooter = (pageNum: number) => {
            pdf.setFillColor(249, 115, 22);
            pdf.rect(0, 0, pdfWidth, 10, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.text(`${pName} - ${title}`, 10, 6);
            pdf.text(`Page ${pageNum}`, pdfWidth - 20, 6);

            pdf.setFillColor(15, 23, 42);
            pdf.rect(0, pdfHeight - 8, pdfWidth, 8, 'F');
            pdf.setTextColor(200, 200, 200);
            pdf.text('Architecture Documentation Framework | DocSprintAI Platform', 10, pdfHeight - 3);
        };

        let currentPage = 2;
        addHeaderFooter(currentPage);

        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, margin, yPos);
        yPos += 15;

        requirements.forEach((req, index) => {
            // Check for page overflow
            if (yPos > pdfHeight - 40) {
                pdf.addPage();
                currentPage++;
                addHeaderFooter(currentPage);
                yPos = 30;
            }

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(15, 23, 42);
            const titleLines = pdf.splitTextToSize(`${index + 1}. ${req.title}`, pdfWidth - (margin * 2));
            pdf.text(titleLines, margin, yPos);
            yPos += (titleLines.length * 6);

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(20, 20, 20); // Darker color for clarity
            const descLines = pdf.splitTextToSize(stripHtml(req.description || 'No description provided.'), pdfWidth - (margin * 2 + 10));
            descLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage(); currentPage++; addHeaderFooter(currentPage); yPos = 30;
                    pdf.setTextColor(20, 20, 20); // Reset color after footer
                    pdf.setFontSize(10); // Reset Size
                }
                pdf.text(line, margin + 5, yPos);
                yPos += 5;
            });
            yPos += 7;

            // PRD Specific Fields
            if (req.type === 'PRD') {
                if (req.personas) {
                    if (yPos > pdfHeight - 30) {
                        pdf.addPage(); currentPage++; addHeaderFooter(currentPage); yPos = 30;
                    }
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(10); // Check font size
                    pdf.setTextColor(20, 20, 20);
                    pdf.text('Target Personas:', margin + 5, yPos);
                    yPos += 6;
                    pdf.setFont('helvetica', 'normal');
                    const personaLines = pdf.splitTextToSize(stripHtml(req.personas), pdfWidth - (margin * 2 + 10));
                    personaLines.forEach((line: string) => {
                        if (yPos > pdfHeight - 25) {
                            pdf.addPage(); currentPage++; addHeaderFooter(currentPage); yPos = 30;
                            pdf.setTextColor(20, 20, 20);
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(10); // Reset Size
                        }
                        pdf.text(line, margin + 5, yPos);
                        yPos += 5;
                    });
                    yPos += 8;
                }
                if (req.screens) {
                    if (yPos > pdfHeight - 30) {
                        pdf.addPage(); currentPage++; addHeaderFooter(currentPage); yPos = 30;
                    }
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(10); // Check font size
                    pdf.setTextColor(20, 20, 20);
                    pdf.text('Key Screens:', margin + 5, yPos);
                    yPos += 6;
                    pdf.setFont('helvetica', 'normal');
                    const screenLines = pdf.splitTextToSize(stripHtml(req.screens), pdfWidth - (margin * 2 + 10));
                    screenLines.forEach((line: string) => {
                        if (yPos > pdfHeight - 25) {
                            pdf.addPage(); currentPage++; addHeaderFooter(currentPage); yPos = 30;
                            pdf.setTextColor(20, 20, 20);
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(10); // Reset Size
                        }
                        pdf.text(line, margin + 5, yPos);
                        yPos += 5;
                    });
                    yPos += 8;
                }
            }
        });

        return pdf.output('blob');
    };

    const handleDownloadCurrentTab = async () => {
        if (!project || ['project', 'estimation', 'test-cases', 'token-usage'].includes(activeTab)) return;
        setIsExporting(true);
        try {
            const filteredReqs = project.requirements?.filter(r => r.type === activeTab.toUpperCase()) || [];
            const tabName = navigationItems.find(n => n.id === activeTab)?.name || activeTab;
            const blob = await generatePDFBlob(tabName, filteredReqs, project.name);
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
            await saveFileWithPicker(blob, `${project.name}_${activeTab.toUpperCase()}_Requirements_${timestamp}.pdf`, 'application/pdf', '.pdf');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownloadFullProject = async () => {
        if (!project) return;
        setIsExporting(true);
        const zip = new JSZip();
        try {
            const reqTypes = [
                { type: 'BRD', name: 'Business Requirements' },
                { type: 'PRD', name: 'Product Requirements' },
                { type: 'NFR', name: 'Non Functional Requirements' },
                { type: 'UIR', name: 'User Interface Requirements' },
                { type: 'BP', name: 'Business Processes' }
            ];

            for (const rt of reqTypes) {
                const reqs = project.requirements?.filter(r => r.type === rt.type) || [];
                if (reqs.length > 0) {
                    const blob = await generatePDFBlob(rt.name, reqs, project.name);
                    zip.file(`${rt.type}_Requirements.pdf`, blob);
                }
            }

            // Generate Implementation Packs for all PRDs
            const prds = project.requirements?.filter(r => r.type === 'PRD') || [];
            if (prds.length > 0) {
                const implementationFolder = zip.folder("Implementation_Packs");
                if (implementationFolder) {
                    toast.loading('Compressing Implementation Data...', { duration: 2000 });
                    for (const prd of prds) {
                        try {
                            const storiesRes = await userStoryService.getUserStories({ requirementId: prd.id });
                            const stories = storiesRes.data?.data || [];
                            const blob = await generateImplementationPDF(prd, stories, project.name);
                            const sanitizedTitle = prd.title.replace(/[/\\?%*:|"<>]/g, '-').slice(0, 50);
                            implementationFolder.file(`${sanitizedTitle}_Implementation.pdf`, blob);
                        } catch (err) {
                            console.error(`Failed to pack implementation for ${prd.title}`, err);
                        }
                    }
                }
            }

            // Generate Test Cases for all User Stories
            const testCasesFolder = zip.folder("Test_Cases");
            if (testCasesFolder) {
                toast.loading('Compressing Test Cases...', { duration: 2000 });
                try {
                    // Get all user stories across all PRDs
                    const allStoriesPromises = prds.map(prd =>
                        userStoryService.getUserStories({ requirementId: prd.id })
                            .then(res => res.data?.data || [])
                            .catch(() => [])
                    );
                    const allStoriesArrays = await Promise.all(allStoriesPromises);
                    const allStories = allStoriesArrays.flat();

                    // For each user story, get and generate test cases PDF
                    for (const story of allStories) {
                        try {
                            const testCasesRes = await testCaseService.getTestCases({ userStoryId: story.id });
                            const testCases = testCasesRes.data || [];

                            if (testCases.length > 0) {
                                const title = `Test Cases for ${story.title}`;
                                const blob = await generateTestCasesBulkPDF(testCases, title, project.name);
                                const sanitizedStoryTitle = story.title.replace(/[/\\?%*:|"<>]/g, '-').slice(0, 50);
                                testCasesFolder.file(`${sanitizedStoryTitle}_TestCases.pdf`, blob);
                            }
                        } catch (err) {
                            console.error(`Failed to pack test cases for ${story.title}`, err);
                        }
                    }
                } catch (err) {
                    console.error('Failed to generate test cases folder', err);
                }
            }

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
            const content = await zip.generateAsync({ type: 'blob' });
            await saveFileWithPicker(content, `${project.name}_Full_Documentation_${timestamp}.zip`, 'application/zip', '.zip');
        } catch (error) {
            console.error('ZIP Error:', error);
            toast.error('Failed to generate Documentation zip');
        } finally {
            setIsExporting(false);
        }
    };


    const selectedRequirement = project?.requirements?.find(r => r.id === selectedReqId)
    const currentLinks = selectedReqId ? (requirementLinks[selectedReqId] || []) : []

    // Reset selected requirement when switching tabs
    useEffect(() => {
        setSelectedReqId(null)
        setIsLinking(false)
    }, [activeTab])

    useEffect(() => {
        if (project?.businessProcess?.mermaidCode) {
            setBpCode(project.businessProcess.mermaidCode)
        } else {
            setBpCode('')
        }
        setIsEditingBp(false)
    }, [project, activeTab])

    useEffect(() => {
        if (selectedReqId && activeTab === 'prd') {
            setRightSidebarTab('link')
        } else {
            setRightSidebarTab('chat')
        }

        if (selectedRequirement) {
            setEditTitle(selectedRequirement.title || '')
            setEditParentId(selectedRequirement.parentId || null)
            setSelectedLinkedIds(selectedRequirement.linkedIds || [])
            const baseDesc = selectedRequirement.description || ''
            const extra = activeTab === 'prd' ? `\n\nScreens:\n${selectedRequirement.screens || 'N/A'}\n\nPersonas:\n${selectedRequirement.personas || 'N/A'}` : ''
            setEditDescription(baseDesc + extra)
        } else if (selectedReqId === 'new') {
            setEditTitle(`New ${activeTab.toUpperCase()} Requirement`)
            setEditDescription('')
            setEditParentId(null)
        }

        if (!selectedReqId) {
            setIsLinking(false)
            setLinkSearchTerm('')
        }
    }, [selectedRequirement, selectedReqId, activeTab])



    useEffect(() => {
        const fetchStories = async () => {
            if (selectedReqId && activeTab === 'prd' && selectedReqId !== 'new') {
                setIsLoadingStories(true)
                try {
                    const response = await userStoryService.getUserStories({ requirementId: selectedReqId })
                    // AdonisJS Paginator: { data: { data: [...], meta: {...} } }
                    setSelectedUserStories(response.data?.data || [])
                } catch (err) {
                    console.error('Failed to fetch stories:', err)
                } finally {
                    setIsLoadingStories(false)
                }
            } else {
                setSelectedUserStories([])
            }
        }
        fetchStories()
    }, [selectedReqId, activeTab])

    // Test Cases View Effects
    useEffect(() => {
        if (activeTab === 'test-cases' && project?.requirements) {
            const prds = project.requirements.filter(r => r.type === 'PRD');
            if (prds.length > 0 && !testCaseViewPrdId) {
                setTestCaseViewPrdId(prds[0].id);
            }
        }
    }, [activeTab, project, testCaseViewPrdId]);

    useEffect(() => {
        const fetchStoriesAndCounts = async () => {
            if (activeTab === 'test-cases' && testCaseViewPrdId) {
                setIsLoadingStories(true);
                try {
                    const storiesRes = await userStoryService.getUserStories({ requirementId: testCaseViewPrdId });
                    const stories = storiesRes.data?.data || [];

                    // Try to fetch all test cases for the project to count
                    // If backend doesn't support project-level fetch efficiently, we might need a different approach
                    // defaulting to 0 for now if fetch fails or just counting what we get
                    let allTestCases: any[] = [];
                    try {
                        if (id) {
                            const allTestCasesRes = await testCaseService.getTestCases({ projectId: id });
                            allTestCases = allTestCasesRes.data || [];
                        }
                    } catch (err) {
                        console.warn('Could not fetch project-wide test cases, counts may be 0', err);
                    }

                    const mapped = stories.map((story: UserStory) => ({
                        story,
                        count: allTestCases.filter((tc: any) => tc.userStoryId === story.id).length
                    }));

                    setStoriesWithTestCounts(mapped);
                } catch (e) {
                    console.error(e);
                    toast.error('Failed to load user stories stats');
                } finally {
                    setIsLoadingStories(false);
                }
            }
        };
        fetchStoriesAndCounts();
    }, [activeTab, testCaseViewPrdId, id]);

    useEffect(() => {
        const fetchTokenData = async () => {
            if (activeTab === 'token-usage') {
                setIsLoadingTokenData(true)
                try {
                    const [usageRes, historyRes] = await Promise.all([
                        authService.getTokenUsage(),
                        authService.getTokenHistory({ projectId: id || undefined })
                    ])
                    setTokenUsage(usageRes.data)
                    setTokenHistory(historyRes.data?.data || [])
                } catch (err) {
                    console.error('Failed to fetch token data:', err)
                    toast.error('Failed to load usage statistics')
                } finally {
                    setIsLoadingTokenData(false)
                }
            }
        }
        fetchTokenData()
    }, [activeTab, id])

    useEffect(() => {
        let pollInterval: NodeJS.Timeout

        const fetchIntelligenceData = async () => {
            if (activeTab === 'product-intelligence' && id) {
                if (!intelligenceData) setIsLoadingIntelligence(true)
                try {
                    const response = await productIntelligenceService.getOverview(id)
                    setIntelligenceData(response.data)

                    if (['pending', 'processing'].includes(response.data.status)) {
                        setIsPollingIntelligence(true)
                    } else {
                        setIsPollingIntelligence(false)
                    }
                } catch (err) {
                    console.error('Failed to fetch product intelligence data:', err)
                } finally {
                    setIsLoadingIntelligence(false)
                }
            }
        }

        fetchIntelligenceData()

        if (isPollingIntelligence && activeTab === 'product-intelligence') {
            pollInterval = setInterval(() => {
                pollCountRef.current += 1
                fetchIntelligenceData()

                // 4 polls * 30 seconds = 2 minutes
                if (pollCountRef.current >= 4) {
                    setIsPollingIntelligence(false)
                    setShowDeepScanMessage(true)
                    clearInterval(pollInterval)
                }
            }, 30000) // Poll every 30s
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval)
        }
    }, [activeTab, id, isPollingIntelligence])

    const handleRunIntelligenceAnalysis = async () => {
        if (!id) return;

        pollCountRef.current = 0;
        setShowDeepScanMessage(false);
        const loadingToast = toast.loading('Initializing Market Intelligence workers...')
        try {
            await productIntelligenceService.analyze(id);
            toast.success('Analysis job queued successfully! This might take a few minutes.', { id: loadingToast })
            setIsPollingIntelligence(true)
            // Immediately fetch update
            const response = await productIntelligenceService.getOverview(id)
            setIntelligenceData(response.data)
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to start analysis.', { id: loadingToast })
        }
    }

    const handleStopIntelligenceAnalysis = async () => {
        if (!id) return;
        const loadingToast = toast.loading('Stopping analysis...')
        try {
            await productIntelligenceService.cancelAnalysis(id)
            setIsPollingIntelligence(false)
            // Refresh status so UI shows failed/stopped state
            const response = await productIntelligenceService.getOverview(id)
            setIntelligenceData(response.data)
            toast.success('Analysis stopped. You can re-run it at any time.', { id: loadingToast })
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to stop analysis.', { id: loadingToast })
        }
    }

    const generateImplementationPDF = async (prd: Requirement, stories: UserStory[], pName: string) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;

        // 1. Cover Page
        pdf.setFillColor(88, 28, 135); // Deep Purple for Implementation Pack
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(40);
        pdf.setFont('helvetica', 'bold');
        pdf.text('IMPLEMENTATION', 20, 100);
        pdf.text('SPECIFICATION', 20, 115);

        pdf.setDrawColor(255, 255, 255);
        pdf.line(20, 125, 120, 125);

        pdf.setFontSize(18);
        pdf.text(pName.toUpperCase(), 20, 140);
        pdf.setFontSize(14);
        pdf.text(`PRD: ${prd.title}`, 20, 150);
        pdf.setFontSize(10);
        pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 165);

        // Header/Footer Helper
        const addHF = (pageNum: number) => {
            pdf.setFillColor(88, 28, 135);
            pdf.rect(0, 0, pdfWidth, 10, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.text(`${pName} | Implementation Plan`, 10, 6);
            pdf.text(`Page ${pageNum}`, pdfWidth - 20, 6);
            pdf.setFillColor(15, 23, 42);
            pdf.rect(0, pdfHeight - 8, pdfWidth, 8, 'F');
            pdf.text('DocSprintAI Implementation Framework | Confidential', 10, pdfHeight - 3);
        };

        // 2. PRD Content
        pdf.addPage();
        let currentPage = 2;
        addHF(currentPage);
        let yPos = 30;

        pdf.setTextColor(88, 28, 135);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('1. Product Requirement Context', margin, yPos);
        yPos += 15;

        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(12);
        const safeTitle = prd.title || 'Untitled Requirement';
        pdf.text(safeTitle, margin, yPos);
        yPos += 10;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(20, 20, 20);
        const descLines = pdf.splitTextToSize(stripHtml(prd.description || 'No description provided.'), pdfWidth - (margin * 2));
        descLines.forEach((line: string) => {
            if (yPos > pdfHeight - 25) {
                pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                pdf.setTextColor(20, 20, 20); // Reset color
                pdf.setFontSize(10); // Reset Size
            }
            pdf.text(line, margin, yPos);
            yPos += 5;
        });
        yPos += 8;

        // Custom Fields
        if (prd.personas) {
            if (yPos > pdfHeight - 30) { pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30; }
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(15, 23, 42); // Navy for header
            pdf.text('Target Personas:', margin, yPos);
            yPos += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(20, 20, 20); // Dark for content
            const personaLines = pdf.splitTextToSize(stripHtml(prd.personas), pdfWidth - (margin * 2));
            personaLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                    pdf.setTextColor(20, 20, 20); // Reset color
                    pdf.setFontSize(10);
                }
                pdf.text(line, margin, yPos);
                yPos += 5;
            });
            yPos += 8;
        }

        if (prd.screens) {
            if (yPos > pdfHeight - 30) { pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30; }
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(15, 23, 42);
            pdf.text('Key Screens:', margin, yPos);
            yPos += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(20, 20, 20);
            const screenLines = pdf.splitTextToSize(stripHtml(prd.screens), pdfWidth - (margin * 2));
            screenLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                    pdf.setTextColor(20, 20, 20); // Reset color
                    pdf.setFontSize(10);
                }
                pdf.text(line, margin, yPos);
                yPos += 5;
            });
            yPos += 8;
        }

        yPos += 10;

        // 3. User Stories & Tasks
        stories.forEach((story, sIndex) => {
            if (yPos > pdfHeight - 60) {
                pdf.addPage();
                currentPage++;
                addHF(currentPage);
                yPos = 30;
            }

            pdf.setTextColor(88, 28, 135);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Story ${sIndex + 1}: ${story.title || 'Untitled'}`, margin, yPos);
            yPos += 8;

            pdf.setTextColor(15, 23, 42);
            pdf.setFontSize(10);
            pdf.text(`Persona: ${story.persona || 'General'} | Points: ${story.storyPoints || 0}`, margin, yPos);
            yPos += 10;

            pdf.setFont('helvetica', 'italic');
            const storyDescLines = pdf.splitTextToSize(story.description || 'No description.', pdfWidth - (margin * 2 + 5));
            storyDescLines.forEach((line: string) => {
                if (yPos > pdfHeight - 25) {
                    pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                    pdf.setTextColor(15, 23, 42); // Ensure color
                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'italic');
                }
                pdf.text(line, margin + 5, yPos);
                yPos += 5;
            });
            yPos += 8;

            if (story.acceptanceCriteria) {
                if (yPos > pdfHeight - 30) { pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30; }
                pdf.setFont('helvetica', 'bold');
                pdf.text('Acceptance Criteria:', margin + 5, yPos);
                yPos += 6;
                pdf.setFont('helvetica', 'normal');

                let acText = '';
                if (Array.isArray(story.acceptanceCriteria)) {
                    acText = story.acceptanceCriteria.map(i => `• ${i.replace(/^"|"$/g, '')}`).join('\n');
                } else {
                    let raw = String(story.acceptanceCriteria || '').trim();
                    // Clean postgres format {"A","B"}
                    if (raw.startsWith('{') && raw.endsWith('}')) {
                        raw = raw.substring(1, raw.length - 1); // remove {}
                        const matches = raw.match(/"([^"]+)"/g);
                        if (matches) {
                            acText = matches.map(m => `• ${m.replace(/"/g, '')}`).join('\n');
                        } else {
                            acText = raw.split(',').map(s => `• ${s.trim()}`).join('\n');
                        }
                    } else {
                        acText = raw.split('\n').map(s => s.trim() ? `• ${s}` : '').join('\n');
                    }
                }

                const acLines = pdf.splitTextToSize(acText, pdfWidth - (margin * 2 + 10));
                acLines.forEach((line: string) => {
                    if (yPos > pdfHeight - 25) {
                        pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                        pdf.setTextColor(15, 23, 42); // Reset color
                        pdf.setFontSize(10);
                        pdf.setFont('helvetica', 'normal');
                    }
                    pdf.text(line, margin + 10, yPos);
                    yPos += 5;
                });
                yPos += 8;
            }

            if (story.tasks && story.tasks.length > 0) {
                if (yPos > pdfHeight - 30) { pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30; }
                pdf.setFont('helvetica', 'bold');
                pdf.text('Implementation Tasks:', margin + 5, yPos);
                yPos += 6;
                pdf.setFont('helvetica', 'normal');
                story.tasks.forEach((task: any) => {
                    const taskTitle = task.title || 'Untitled Task';
                    const taskHours = Number(task.estimatedHours || task.estimated_hours || 0);
                    const timeStr = taskHours > 0 ? ` - ${taskHours}h` : '';
                    const fullLine = `• ${taskTitle}${timeStr}`;

                    const taskLines = pdf.splitTextToSize(fullLine, pdfWidth - (margin * 2 + 10));
                    taskLines.forEach((line: string) => {
                        if (yPos > pdfHeight - 25) {
                            pdf.addPage(); currentPage++; addHF(currentPage); yPos = 30;
                            pdf.setTextColor(15, 23, 42);
                            pdf.setFontSize(10);
                            pdf.setFont('helvetica', 'normal');
                        }
                        pdf.text(line, margin + 10, yPos);
                        yPos += 5;
                    });
                    yPos += 2; // small gap between tasks
                });
                yPos += 8;
            }
        });

        if (stories.length === 0) {
            pdf.setTextColor(88, 28, 135);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('2. User Stories & Implementation', margin, yPos);
            yPos += 10;

            pdf.setTextColor(71, 85, 105);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            pdf.text('No User Stories or tasks have been generated for this requirement yet.', margin, yPos);
            pdf.text('Use the "Generate User Stories" feature to create implementation details.', margin, yPos + 6);
        }


        return pdf.output('blob');
    };

    const handleDownloadPRDImplementation = async (prdId: string) => {
        const prd = project?.requirements?.find(r => r.id === prdId);
        if (!prd || !project) return;
        setIsExporting(true);
        try {
            const storiesRes = await userStoryService.getUserStories({ requirementId: prdId });
            // Extract from paginator
            const stories = storiesRes.data?.data || [];

            if (stories.length === 0) {
                toast('No user stories found. Downloading PRD context only.', { icon: 'ℹ️' });
            }

            const blob = await generateImplementationPDF(prd, stories, project.name);
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
            const sanitizedPrdTitle = prd.title.replace(/[/\\?%*:|"<>]/g, '-').slice(0, 50);
            await saveFileWithPicker(blob, `${project.name}_${sanitizedPrdTitle}_Implementation_Pack_${timestamp}.pdf`, 'application/pdf', '.pdf');
        } catch (error) {
            console.error('PRD Export error:', error);
            toast.error('Failed to generate Implementation Pack');
        } finally {
            setIsExporting(false);
        }
    }

    const handleUserStoryNavigation = async (requirementId: string) => {
        if (!isPro && id) {
            try {
                const eligibility = await projectService.checkAiEligibility(id);
                if (!eligibility.allowed) {
                    window.dispatchEvent(new CustomEvent('plan-limit-reached', {
                        detail: { message: eligibility.message }
                    }));
                    return;
                }
            } catch (error) {
                console.error('Eligibility check failed:', error);
            }
        }
        navigate(`/requirements/${requirementId}/user-stories`);
    };

    const handleToggleLink = (brdId: string) => {
        setSelectedLinkedIds(prev =>
            prev.includes(brdId) ? prev.filter(id => id !== brdId) : [...prev, brdId]
        )
    }

    const handleEnhanceRequirement = async () => {
        if (!editDescription) return
        setIsEnhancing(true)
        try {
            const result = await coreService.enhance({
                content: editDescription,
                instruction: `Enhance this ${activeTab.toUpperCase()} requirement description to be more professional, crystalline, and detailed. Maintain the current intent but improve technical depth and clarity.`,
                context: {
                    project: project ? { name: project.name, description: project.description } : undefined,
                    requirementType: activeTab.toUpperCase()
                }
            })

            if (result.success && result.data.editedContent) {
                setEditDescription(result.data.editedContent)
                toast.success('Requirement enhanced with AI!');
            } else {
                console.warn('Enhancement resulted in no content or failed:', result);
                toast.error('AI could not enhance this description. Try simpler text.');
            }

        } catch (err: any) {
            console.error('Enhancement failed:', err)
            if (err.response?.status === 429) {
                toast.error('Daily AI token limit reached. Please wait for reset.');
            } else {
                const msg = err.response?.data?.error?.message || err.message || 'Unknown error'
                toast.error(`AI Enhancement Failed: ${msg}`);
            }
        } finally {
            setIsEnhancing(false)
        }
    }

    const handleUpdateRequirement = async () => {
        if (!selectedReqId || !id) return
        try {
            if (selectedReqId === 'new') {
                const type = activeTab.toUpperCase()
                const response = await requirementService.createRequirement({
                    projectId: id,
                    type: type,
                    title: editTitle,
                    description: editDescription,
                    parentId: editParentId,
                    linkedIds: selectedLinkedIds,
                    status: 'draft'
                })

                if (response && response.data) {
                    if (id) dispatch(getProjectDetailsRequest(id));
                }
            } else {
                await requirementService.updateRequirement(selectedReqId, {
                    title: editTitle,
                    description: editDescription,
                    parentId: editParentId,
                    linkedIds: selectedLinkedIds
                })
                if (id) dispatch(getProjectDetailsRequest(id));
            }
            toast.success(`${selectedReqId === 'new' ? 'Requirement created' : 'Requirement updated'} successfully!`)
            if (selectedReqId === 'new') {
                setSelectedReqId(null)
            }
        } catch (err) {
            console.error('Failed to save requirement:', err)
            toast.error('Failed to save requirement')
        }
    }

    const handleDeleteRequirement = async () => {
        if (!selectedReqId) return
        setIsDeleting(true)
        try {
            await requirementService.deleteRequirement(selectedReqId)
            if (id) dispatch(getProjectDetailsRequest(id));
            setSelectedReqId(null)
            setShowDeleteModal(false)
            toast.success('Requirement deleted successfully')
        } catch (err) {
            console.error('Failed to delete requirement:', err)
            toast.error('Failed to delete requirement')
        } finally {
            setIsDeleting(false)
        }
    }

    const handleGenerateBatch = async () => {
        if (!id) return
        setIsGeneratingBatch(true)
        const loadingToast = isRegenerateMode ? toast.loading(`Regenerating ${genType} list...`) : null
        try {
            const payload: any = { type: genType, count: genCount, clearExisting: isRegenerateMode }
            if (isRegenerateMode && userThoughts) {
                payload.userThoughts = userThoughts
            }
            await projectService.generateRequirementBatch(id, payload)

            const response = await projectService.getProject(id)
            if (id) dispatch(getProjectDetailsRequest(id));

            if (isRegenerateMode) {
                toast.success(`${genType} list regenerated successfully!`, { id: loadingToast! })
            }
            if (!isRegenerateMode) {
                setGenCount(15)
            }
            setUserThoughts('')
            setIsGenModalOpen(false)
            if (genType === 'BP') {
                toast.success('Business Processes generated!')
            }
        } catch (err) {
            console.error('Failed to generate requirements:', err)
            toast.error('Failed to generate requirements', isRegenerateMode ? { id: loadingToast! } : undefined)
        } finally {
            setIsGeneratingBatch(false)
        }
    }

    const handleRegenerateBatch = async (type: string) => {
        openGenModal(type as any, true)
    }

    const openGenModal = (type: 'BRD' | 'PRD' | 'NFR' | 'UIR' | 'BP', isRegenerate = false) => {
        setGenType(type)
        setGenCount(15)
        setIsRegenerateMode(isRegenerate)
        setIsGenModalOpen(true)
    }

    const handleGenerateBusinessProcess = async () => {
        if (!id) return
        setIsGeneratingBp(true)
        const loadingToast = toast.loading('Generating Business Process flowchart via AI...')
        try {
            const res = await projectService.generateBusinessProcess(id)
            if (res && res.success) {
                toast.success('Business Process flowchart generated successfully!', { id: loadingToast })
                dispatch(getProjectDetailsRequest(id))
            } else {
                toast.error(res.message || 'Failed to generate Business Process flowchart', { id: loadingToast })
            }
        } catch (err: any) {
            console.error('Failed to generate business process flowchart:', err)
            toast.error(err.response?.data?.message || 'Failed to generate Business Process flowchart', { id: loadingToast })
        } finally {
            setIsGeneratingBp(false)
        }
    }

    const handleSaveBusinessProcess = async () => {
        if (!id) return
        const loadingToast = toast.loading('Saving Business Process flowchart...')
        try {
            const res = await projectService.saveBusinessProcess(id, bpCode)
            if (res && res.success) {
                toast.success('Business Process flowchart saved successfully!', { id: loadingToast })
                setIsEditingBp(false)
                dispatch(getProjectDetailsRequest(id))
            } else {
                toast.error(res.message || 'Failed to save Business Process flowchart', { id: loadingToast })
            }
        } catch (err: any) {
            console.error('Failed to save business process:', err)
            toast.error('Failed to save Business Process flowchart', { id: loadingToast })
        }
    }

    // State to track last used pool for variety
    const [lastPoolIndex, setLastPoolIndex] = useState<number | null>(null)

    const handleRegenerateFlow = async () => {
        setIsGeneratingBatch(true)
        const themes: ('indigo' | 'purple' | 'emerald' | 'orange' | 'cyan' | 'rose')[] = ['indigo', 'purple', 'emerald', 'orange', 'cyan', 'rose']
        const newTheme = themes[Math.floor(Math.random() * themes.length)]

        const linkedReqs = project?.requirements?.filter(r => selectedLinkedIds.includes(r.id)) || []
        const prds = linkedReqs.filter(r => r.type === 'PRD').map(r => r.title)
        const brds = linkedReqs.filter(r => r.type === 'BRD').map(r => r.title)

        setTimeout(() => {
            const pools = [
                // Pool 0: Standard SDLC (Nested terminal)
                [
                    { title: `Initialize: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: prds[0] || 'Feature Discovery & Mapping', type: 'step', layout: 'vertical' },
                    {
                        title: 'UI/UX Validated?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Build Core Components', type: 'step', layout: 'vertical' },
                            { title: 'Backend Integration', type: 'step', layout: 'vertical' },
                            {
                                title: 'Performance OK?',
                                type: 'decision',
                                layout: 'fork',
                                yes: [
                                    { title: 'Integration Testing', type: 'step', layout: 'vertical' },
                                    { title: 'Deploy to Staging', type: 'step', layout: 'vertical' },
                                    { title: 'Production Release', type: 'step', layout: 'vertical' }
                                ],
                                no: [
                                    { title: 'Optimize & Cache', type: 'step', layout: 'vertical' },
                                    { title: 'Hotfix Deployment', type: 'step', layout: 'vertical' },
                                    { title: 'Re-deploy to Staging', type: 'step', layout: 'vertical' }
                                ]
                            }
                        ],
                        no: [
                            { title: 'Re-wireframe & Test', type: 'step', layout: 'vertical' },
                            { title: 'Stakeholder Review', type: 'step', layout: 'vertical' },
                            { title: 'Kill Project Seed', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 1: Business Growth (Nested terminal)
                [
                    { title: `Launch Initiative: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: brds[0] || 'Strategic Goal Alignment', type: 'step', layout: 'vertical' },
                    {
                        title: 'Decision: ROI Validated?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Expand Market Scope', type: 'step', layout: 'vertical' },
                            {
                                title: 'Market Launch Ready?',
                                type: 'decision',
                                layout: 'fork',
                                yes: [
                                    { title: 'Global Rollout', type: 'step', layout: 'vertical' },
                                    { title: 'Real-time Analytics Monitoring', type: 'step', layout: 'vertical' },
                                    { title: `Goal: ${editTitle} Profitability`, type: 'step', layout: 'vertical' }
                                ],
                                no: [
                                    { title: 'Phase-1 Pilot Only', type: 'step', layout: 'vertical' },
                                    { title: 'Incremental Scaling', type: 'step', layout: 'vertical' },
                                    { title: 'Strategy Post-Mortem', type: 'step', layout: 'vertical' }
                                ]
                            }
                        ],
                        no: [
                            { title: 'Refine Strategy', type: 'step', layout: 'vertical' },
                            { title: 'Legal & Compliance Audit', type: 'step', layout: 'vertical' },
                            { title: 'Re-evaluating Market Fit', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 2: Rapid Prototyping (Nested terminal)
                [
                    { title: `Quick-Start: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Low-Fidelity Mockup Generation', type: 'step', layout: 'vertical' },
                    {
                        title: 'User Feedback Positive?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Move to High-Fi Mockups', type: 'step', layout: 'vertical' },
                            {
                                title: 'Bug Count < 10?',
                                type: 'decision',
                                layout: 'fork',
                                yes: [
                                    { title: 'Beta Release', type: 'step', layout: 'vertical' },
                                    { title: 'Closed Beta Feedback Collectors', type: 'step', layout: 'vertical' },
                                    { title: `MVP Launch: ${editTitle}`, type: 'step', layout: 'vertical' }
                                ],
                                no: [
                                    { title: 'Extended QA Cycle', type: 'step', layout: 'vertical' },
                                    { title: 'Refactoring Core Logic', type: 'step', layout: 'vertical' },
                                    { title: 'Re-validate MVP Scope', type: 'step', layout: 'vertical' }
                                ]
                            }
                        ],
                        no: [
                            { title: 'Pivoting Strategy', type: 'step', layout: 'vertical' },
                            { title: 'Gather New Persona Insights', type: 'step', layout: 'vertical' },
                            { title: 'Redesigning Prototype', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 3: Security & Compliance (Deep Linear terminal)
                [
                    { title: `Security Review: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Vulnerability Assessment', type: 'step', layout: 'vertical' },
                    {
                        title: 'Critical Flaws Found?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Emergency Patch Sequence', type: 'step', layout: 'vertical' },
                            { title: 'Remediation Verification', type: 'step', layout: 'vertical' },
                            { title: 'Infrastructure Hardening', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'External Security Audit', type: 'step', layout: 'vertical' },
                            {
                                title: 'Audit Passed?',
                                type: 'decision',
                                layout: 'fork',
                                yes: [
                                    { title: 'Issue Compliance Certificate', type: 'step', layout: 'vertical' },
                                    { title: 'Production Go-Live', type: 'step', layout: 'vertical' }
                                ],
                                no: [
                                    { title: 'Audit Remediation Phase', type: 'step', layout: 'vertical' },
                                    { title: 'Final Security Sign-off', type: 'step', layout: 'vertical' }
                                ]
                            }
                        ]
                    }
                ],
                // Pool 4: Scale & Optimization (Recursive Loop terminal)
                [
                    { title: `Global Optimization: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'System Load Testing', type: 'step', layout: 'vertical' },
                    {
                        title: 'Latency < 200ms?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Apply Vertical Scaling', type: 'step', layout: 'vertical' },
                            { title: 'Edge Computing Distribution', type: 'step', layout: 'vertical' },
                            { title: 'Performance Baseline Met', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Microservices Decomposition', type: 'step', layout: 'vertical' },
                            { title: 'Distributed Cache Implementation', type: 'step', layout: 'vertical' },
                            { title: 'Auto-scaling Policy Finalization', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 5: Marketing & User Acquisition (Terminal Bifurcation)
                [
                    { title: `Marketing Drive: ${editTitle}`, type: 'step', layout: 'vertical' },
                    {
                        title: 'Marketing Strategy?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Paid Ad Campaign Creation', type: 'step', layout: 'vertical' },
                            { title: 'Social Media Influencer Outreach', type: 'step', layout: 'vertical' },
                            { title: 'Conversion Rate Optimization', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Content-Led SEO Growth', type: 'step', layout: 'vertical' },
                            { title: 'Community Engagement Building', type: 'step', layout: 'vertical' },
                            { title: 'Organic Viral Gifting Flow', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 6: Infrastructure Automation (Terminal Mixed)
                [
                    { title: `Automation Build: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Provisioning (Terraform/CloudFormation)', type: 'step', layout: 'vertical' },
                    {
                        title: 'Provisioning Success?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'CI/CD Pipeline Integration', type: 'step', layout: 'vertical' },
                            { title: 'Automated Functional Testing', type: 'step', layout: 'vertical' },
                            { title: 'Observability Stack Setup', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Log Analysis & Bug Hunt', type: 'step', layout: 'vertical' },
                            { title: 'Environment Cleanup', type: 'step', layout: 'vertical' },
                            { title: 'Config Parameter Review', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 7: User Experience Focused (Terminal Linear)
                [
                    { title: `UX Initiative: ${editTitle}`, type: 'step', layout: 'vertical' },
                    {
                        title: 'Target Audience Identified?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Accessibility (A11y) Implementation', type: 'step', layout: 'vertical' },
                            { title: 'Multi-Language Localization', type: 'step', layout: 'vertical' },
                            { title: 'Final Experience Polish', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'User Persona Deep-Dive', type: 'step', layout: 'vertical' },
                            { title: 'Market Sentiment Sampling', type: 'step', layout: 'vertical' },
                            { title: 'Re-define Brand Guidelines', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 8: FinTech & Payment Security (Terminal)
                [
                    { title: `Finance Engine: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Fraud Detection Training', type: 'step', layout: 'vertical' },
                    {
                        title: 'Risk Profile Acceptable?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'PCI-DSS Scoping Phase', type: 'step', layout: 'vertical' },
                            { title: 'Live Key Rotating Setup', type: 'step', layout: 'vertical' },
                            { title: 'KYC/AML System Handshake', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Manual Risk Mitigation', type: 'step', layout: 'vertical' },
                            { title: 'Policy Adjustments', type: 'step', layout: 'vertical' },
                            { title: 'Halt Transaction Engine', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 9: AI & Data Science (Terminal Iterative)
                [
                    { title: `AI Training: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Data Pipeline Engineering', type: 'step', layout: 'vertical' },
                    {
                        title: 'Data Quality Check?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Model Hyperparameter Tuning', type: 'step', layout: 'vertical' },
                            { title: 'A/B Inference Testing', type: 'step', layout: 'vertical' },
                            { title: 'Production Inference Serving', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Dataset Re-scraping', type: 'step', layout: 'vertical' },
                            { title: 'Feature Engineering Sprint', type: 'step', layout: 'vertical' },
                            { title: 'Re-train Model V2', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 10: Logistics & Supply Chain (Terminal Hub)
                [
                    { title: `Logistics Node: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Warehouse API Connector', type: 'step', layout: 'vertical' },
                    {
                        title: 'Inventory Sync?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [
                            { title: 'Route Optimization Algorithm', type: 'step', layout: 'vertical' },
                            { title: 'Dispatch Notification Engine', type: 'step', layout: 'vertical' },
                            { title: 'Real-time Tracking Dashboard', type: 'step', layout: 'vertical' }
                        ],
                        no: [
                            { title: 'Manual Audit Cycle', type: 'step', layout: 'vertical' },
                            { title: 'Staging Error Handling', type: 'step', layout: 'vertical' },
                            { title: 'Retry Connection Hub', type: 'step', layout: 'vertical' }
                        ]
                    }
                ],
                // Pool 11: Enterprise Scale-up (Long Linear)
                [
                    { title: `Scale-up Strategy: ${editTitle}`, type: 'step', layout: 'vertical' },
                    { title: 'Corporate Identity Mapping', type: 'step', layout: 'vertical' },
                    { title: 'Stakeholder Buy-in Phase', type: 'step', layout: 'vertical' },
                    { title: 'Global Infrastructure Expansion', type: 'step', layout: 'vertical' },
                    { title: 'Departmental Training Sprints', type: 'step', layout: 'vertical' },
                    { title: 'Final Operational Readiness', type: 'step', layout: 'vertical' }
                ]
            ]

            // Randomly pick a pool but GUARANTEE it's different from the last one
            let finalChoiceIndex;
            do {
                finalChoiceIndex = Math.floor(Math.random() * pools.length);
            } while (finalChoiceIndex === lastPoolIndex);

            let selectedPool = [...pools[finalChoiceIndex]];

            // INTRODUCE VARIETY MUTATION:
            if (Math.random() > 0.6) {
                const globalVariety = [
                    { title: 'Expert Peer Code Review', type: 'step', layout: 'vertical' },
                    { title: 'Stress Testing & Chaos Engineering', type: 'step', layout: 'vertical' },
                    { title: 'Stakeholder Demo & Approval', type: 'step', layout: 'vertical' },
                    { title: 'Documentation Sync & API Specs', type: 'step', layout: 'vertical' },
                    {
                        title: 'Budget Exceeded?',
                        type: 'decision',
                        layout: 'fork',
                        yes: [{ title: 'Scope Reduction Sprint', type: 'step', layout: 'vertical' }],
                        no: [{ title: 'Optimize Operating Costs', type: 'step', layout: 'vertical' }]
                    }
                ];
                const mutationStep = globalVariety[Math.floor(Math.random() * globalVariety.length)];
                if (selectedPool.length > 2) {
                    const lastIdx = selectedPool.length - 1;
                    if (selectedPool[lastIdx].type !== 'decision') {
                        selectedPool.push(mutationStep);
                    } else if (selectedPool.length > 3) {
                        selectedPool.splice(selectedPool.length - 2, 0, mutationStep);
                    }
                }
            }

            setLastPoolIndex(finalChoiceIndex);
            setFlowSteps(selectedPool);
            setFlowTheme(newTheme as any);

            // SAVE contextually to ID
            if (selectedReqId) {
                setSavedFlows(prev => ({
                    ...prev,
                    [selectedReqId]: { steps: selectedPool, theme: newTheme }
                }))
            }

            setFlowVersion(prev => prev + 1);
            setIsGeneratingBatch(false);
            toast.success(`Workflow regenerated! Unique architectural model applied.`);
        }, 1500);
    }

    const handleDownloadFlow = async () => {
        const captureArea = document.getElementById('workflow-capture-area');
        if (!captureArea) return;

        setIsExporting(true);
        const toastId = toast.loading('Exporting Premium Workflow...');

        try {
            // Force the element to be visible and fully expanded for the capture
            const canvas = await html2canvas(captureArea, {
                scale: 3,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                height: captureArea.scrollHeight, // Force capture of full internal content
                windowHeight: captureArea.scrollHeight,
                onclone: (clonedDoc) => {
                    const area = clonedDoc.getElementById('workflow-capture-area');
                    if (area) {
                        area.style.height = 'auto';
                        area.style.maxHeight = 'none';
                        area.style.overflow = 'visible';
                        // Ensure all parents in the clone allow expansion
                        let parent = area.parentElement;
                        while (parent) {
                            parent.style.height = 'auto';
                            parent.style.overflow = 'visible';
                            parent = parent.parentElement;
                        }
                    }
                }
            });

            const imgData = canvas.toDataURL('image/png', 1.0);
            const imgWidth = 210; // A4
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add padding for branding
            const totalPdfHeight = imgHeight + 40;
            const pdf = new jsPDF({
                orientation: 'p',
                unit: 'mm',
                format: [imgWidth, totalPdfHeight]
            });

            // Premium Header Bar
            pdf.setFillColor(30, 41, 59); // Slate-800
            pdf.rect(0, 0, imgWidth, 20, 'F');

            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.text('BUSINESS PROCESS SPECIFICATION', 15, 12);

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.text(`Project: ${project?.name || 'Specification'}`, imgWidth - 15, 12, { align: 'right' });

            // Subtle watermark or background could go here if needed

            // Main Flow Image
            pdf.addImage(imgData, 'PNG', 0, 25, imgWidth, imgHeight);

            // Premium Footer
            pdf.setFillColor(248, 250, 252); // Slate-50
            pdf.rect(0, totalPdfHeight - 12, imgWidth, 12, 'F');
            pdf.setTextColor(150, 150, 150);
            pdf.setFontSize(8);
            pdf.text('© DOCSPRINT FRAMEWORK', 15, totalPdfHeight - 5);
            pdf.text(`Date: ${new Date().toLocaleDateString()} | Confidential Specification`, imgWidth - 15, totalPdfHeight - 5, { align: 'right' });

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
            const blob = pdf.output('blob');
            await saveFileWithPicker(blob, `${project?.name}_Process_Workflow_${timestamp}.pdf`, 'application/pdf', '.pdf');
            toast.success('Premium workflow exported!', { id: toastId });
        } catch (err) {
            console.error('Workflow Export Error:', err);
            toast.error('Export failed - complex layout detected', { id: toastId });
        } finally {
            setIsExporting(false);
        }
    }



    const handleAddManualRequirement = () => {
        if (!id || activeTab === 'project') return
        const type = activeTab.toUpperCase()
        setEditTitle(`New ${type} Requirement`)
        setEditDescription('')
        setSelectedReqId('new')
    }

    useEffect(() => {
        if (id) {
            dispatch(getProjectDetailsRequest(id))
        }
    }, [id, dispatch])

    const fetchProject = async () => {
        if (!id) return;
        dispatch(getProjectDetailsRequest(id));
    };

    const fetchRequirements = async () => {
        if (!id) return;
        dispatch(getProjectDetailsRequest(id));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (!project) return null

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <LoadingOverlay 
                isOpen={isEnhancing || isGeneratingBatch || isRegenerating || isGeneratingBp} 
                message={
                    isEnhancing ? "Enhancing requirement details with AI..." :
                    isRegenerating ? "Regenerating all requirements from project core..." :
                    isGeneratingBp ? "Designing business process flowchart with AI..." :
                    "Generating requirements with AI engine..."
                } 
            />
            {/* Content Area */}
            <main className={`${selectedReqId ? 'block' : 'glass-panel min-h-[800px] p-8 relative overflow-hidden'}`}>
                {/* Background glow effects for the main content area */}
                {!selectedReqId && (
                    <>
                        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
                        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/10 rounded-full blur-[100px] pointer-events-none" />
                    </>
                )}

                {!selectedReqId && activeTab === 'token-usage' && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300 relative z-10">
                        {/* Header */}
                        <div className="flex justify-between items-start">
                            <div>
                                <h1 className="text-3xl font-black text-white mb-2 flex items-center gap-2 tracking-tight">
                                    Token Usage
                                    <div className="relative flex items-center justify-center p-1.5 rounded-full bg-primary/20">
                                        <div className="absolute inset-0 bg-primary/30 rounded-full blur animate-agent-glow" />
                                        <BoltIcon className="relative h-5 w-5 text-primary animate-pulse-subtle" strokeWidth={2.5} />
                                    </div>
                                </h1>
                                <p className="text-text-secondary font-medium text-sm">Real-time monitoring of your resource consumption for this project.</p>
                            </div>
                            <div className="bg-black/40 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3 backdrop-blur-md shadow-[0_0_15px_rgba(255,107,0,0.1)]">
                                <CpuChipIcon className="h-5 w-5 text-primary" />
                                <div>
                                    <p className="text-[9px] font-black text-primary/70 uppercase tracking-widest leading-none mb-0.5">Powered by</p>
                                    <p className="text-sm font-bold text-white leading-tight">DocSprint Core</p>
                                </div>
                            </div>
                        </div>

                        {isLoadingTokenData ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="h-32 bg-white/5 rounded-2xl animate-pulse border border-white/10" />
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                    <div className="bg-black/30 backdrop-blur-xl p-5 rounded-2xl border border-white/10 hover:border-info/30 transition-all group overflow-hidden relative">
                                        <div className="absolute inset-0 bg-info/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                            <div className="bg-info/10 p-2.5 rounded-xl text-info border border-info/20 shadow-[0_0_15px_rgba(14,165,233,0.2)]">
                                                <ClockIcon className="h-5 w-5" />
                                            </div>
                                            <span className="text-[9px] font-black tracking-widest bg-info/20 text-info px-2 py-0.5 border border-info/30 rounded-full">DAILY</span>
                                        </div>
                                        <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-1 relative z-10">Today's Usage</p>
                                        <h3 className="text-2xl font-black text-white relative z-10">{tokenUsage?.dailyUsed?.toLocaleString() || 0} <span className="text-[10px] text-text-secondary uppercase tracking-wider font-bold">Tokens</span></h3>
                                    </div>

                                    <div className="bg-black/30 backdrop-blur-xl p-5 rounded-2xl border border-white/10 hover:border-green-500/30 transition-all group overflow-hidden relative">
                                        <div className="absolute inset-0 bg-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                            <div className="bg-green-500/10 p-2.5 rounded-xl text-green-400 border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                                                <CircleStackIcon className="h-5 w-5" />
                                            </div>
                                            <span className="text-[9px] font-black tracking-widest bg-green-500/20 text-green-400 px-2 py-0.5 border border-green-500/30 rounded-full">REMAINING</span>
                                        </div>
                                        <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-1 relative z-10">Project Tokens</p>
                                        <h3 className="text-2xl font-black text-white relative z-10">{tokenUsage?.remainingTokens?.toLocaleString() || 0} <span className="text-[10px] text-text-secondary uppercase tracking-wider font-bold">Tokens</span></h3>
                                    </div>

                                    <div className="bg-black/30 backdrop-blur-xl p-5 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all group overflow-hidden relative">
                                        <div className="absolute inset-0 bg-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                            <div className="bg-purple-500/10 p-2.5 rounded-xl text-purple-400 border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.2)]">
                                                <ChartPieIcon className="h-5 w-5" />
                                            </div>
                                            <span className="text-[9px] font-black tracking-widest bg-purple-500/20 text-purple-400 px-2 py-0.5 border border-purple-500/30 rounded-full">UTILIZATION</span>
                                        </div>
                                        <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-1 relative z-10">Resources Used</p>
                                        <h3 className="text-2xl font-black text-white relative z-10">{tokenUsage?.percentageUsed || 0}% <span className="text-[10px] text-text-secondary uppercase tracking-wider font-bold">Capacity</span></h3>
                                    </div>

                                    <div className="relative p-5 rounded-2xl shadow-[0_0_20px_rgba(255,107,0,0.15)] text-white overflow-hidden group">
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary to-orange-700 opacity-90 backdrop-blur-xl" />
                                        <div className="absolute inset-0 bg-white/10 -skew-x-12 -translate-x-full group-hover:animate-[shimmer_2s_infinite] transition-all" />
                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                            <div className="bg-black/20 p-2.5 rounded-xl backdrop-blur-sm border border-white/10 shadow-inner">
                                                <SparklesIcon className="h-5 w-5" />
                                            </div>
                                            <span className="text-[9px] font-black tracking-widest bg-black/20 text-white/90 px-2 py-0.5 border border-white/10 rounded-full">CURRENT PLAN</span>
                                        </div>
                                        <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mb-1 relative z-10">Tier Level</p>
                                        <h3 className="text-2xl font-black tracking-tight relative z-10">{tokenUsage?.limitCondition === 'unlimited' ? 'Pro Unlimited' : 'Free Tier'}</h3>
                                    </div>
                                </div>

                                {/* Usage Visualization */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="lg:col-span-2 bg-black/30 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.3)]">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-base font-black text-white flex items-center gap-2 uppercase tracking-wide">
                                                <CalendarDaysIcon className="h-4 w-4 text-primary" />
                                                Activity Log
                                            </h3>
                                            <button className="text-[10px] font-black text-primary hover:text-orange-400 uppercase tracking-widest transition-colors tracking-widest bg-primary/10 px-3 py-1.5 rounded border border-primary/20">View Matrix</button>
                                        </div>

                                        <div className="overflow-hidden">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-white/5">
                                                        <th className="text-left py-3 text-[10px] font-black text-text-secondary uppercase tracking-widest">Operation</th>
                                                        <th className="text-left py-3 text-[10px] font-black text-text-secondary uppercase tracking-widest">Document</th>
                                                        <th className="text-left py-3 text-[10px] font-black text-text-secondary uppercase tracking-widest">Cost</th>
                                                        <th className="text-left py-3 text-[10px] font-black text-text-secondary uppercase tracking-widest">Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-white/5">
                                                    {tokenHistory.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={4} className="py-12 text-center text-text-secondary italic text-sm font-medium">No usage history recorded for this project yet.</td>
                                                        </tr>
                                                    ) : (
                                                        tokenHistory.map((item) => (
                                                            <tr key={item.id} className="group hover:bg-white/5 transition-all">
                                                                <td className="py-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="bg-black/50 p-2 rounded-lg border border-white/5 group-hover:border-primary/30 transition-all">
                                                                            <CpuChipIcon className="h-4 w-4 text-primary/70 group-hover:text-primary transition-colors" />
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-sm font-bold text-white">{item.modelUsed}</p>
                                                                            <p className="text-[9px] text-text-secondary font-mono tracking-wider">{item.id}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="py-4">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 text-primary text-[9px] font-black border border-primary/20 uppercase tracking-widest">
                                                                        {item.documentType || 'General'}
                                                                    </span>
                                                                </td>
                                                                <td className="py-4">
                                                                    <p className="text-sm font-bold text-white">{item.tokenCount?.toLocaleString()}</p>
                                                                    <p className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">Tokens</p>
                                                                </td>
                                                                <td className="py-4 text-[11px] font-medium text-text-secondary/80">
                                                                    {new Date(item.createdAt).toLocaleDateString()}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="bg-black/40 rounded-2xl p-6 text-white relative overflow-hidden border border-white/5 shadow-inner">
                                        <div className="absolute -bottom-10 -right-10 p-8 opacity-[0.03]">
                                            <BoltIcon className="h-64 w-full text-white rotate-12" />
                                        </div>

                                        <div className="relative z-10">
                                            <h3 className="text-sm font-black mb-6 uppercase tracking-wider border-b border-white/10 pb-3">Usage Allocation</h3>

                                            <div className="space-y-6">
                                                <div>
                                                    <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider mb-2 text-text-secondary">
                                                        <span>Daily Quota</span>
                                                        <span className="text-white">{tokenUsage?.dailyUsed?.toLocaleString()} / {tokenUsage?.dailyLimit?.toLocaleString() || '∞'}</span>
                                                    </div>
                                                    <div className="h-1.5 bg-black/60 rounded-full overflow-hidden border border-white/5">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-orange-600 to-primary rounded-full shadow-[0_0_10px_rgba(255,107,0,0.5)]"
                                                            style={{ width: `${tokenUsage?.percentageUsed || 0}%` }}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider mb-2 text-text-secondary">
                                                        <span>Total Allowance</span>
                                                        <span className="text-white">{tokenUsage && tokenUsage.totalAllowance ? (((tokenUsage.totalAllowance - tokenUsage.remainingTokens) / tokenUsage.totalAllowance) * 100).toFixed(1) : 0}% Used</span>
                                                    </div>
                                                    <div className="h-1.5 bg-black/60 rounded-full overflow-hidden border border-white/5">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-blue-600 to-info rounded-full shadow-[0_0_10px_rgba(14,165,233,0.5)]"
                                                            style={{ width: `${tokenUsage && tokenUsage.totalAllowance ? ((tokenUsage.totalAllowance - tokenUsage.remainingTokens) / tokenUsage.totalAllowance) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="pt-6 mt-6 border-t border-white/10">
                                                    <div className="bg-primary/5 rounded-xl p-4 border border-primary/20 backdrop-blur-sm relative overflow-hidden">
                                                        <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                                                        <p className="text-[11px] text-primary/80 leading-relaxed font-mono">
                                                            <span className="text-primary font-bold">{">"} SYSTEM_NOTE:</span><br />
                                                            "Optimize usage by focusing on high-level requirements first."
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {!selectedReqId && activeTab === 'estimation' && (
                    prerequisitesMet ? (
                        <EstimationCalculator
                            projectId={id!}
                            projectName={project.name}
                            projectDescription={project.description || ''}
                            initialResult={project.estimation ?? null}
                            initialConfig={project.estimationConfig ?? null}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center min-h-[420px] animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="glass-panel rounded-2xl border border-white/10 p-12 flex flex-col items-center gap-6 max-w-lg w-full text-center shadow-[0_0_60px_rgba(0,0,0,0.5)]">
                                <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                                    <LockClosedIcon className="h-8 w-8 text-text-secondary" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-white mb-2">Estimation Calculator Locked</h2>
                                    <p className="text-text-secondary text-sm leading-relaxed">
                                        Complete and verify all prerequisite documents to unlock this module.
                                    </p>
                                </div>
                                <div className="w-full grid grid-cols-2 gap-3">
                                    {(['BRD', 'PRD', 'NFR', 'UIR'] as const).map(type => {
                                        const isDone = verifiedStatuses[type]
                                        return (
                                            <div key={type} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold ${isDone ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-text-secondary'
                                                }`}>
                                                {isDone
                                                    ? <CheckCircleIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                                                    : <XCircleIcon className="h-4 w-4 text-text-secondary/50 shrink-0" />}
                                                {type}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )
                )}
                {/* Market Intelligence View */}
                {!selectedReqId && activeTab === 'product-intelligence' && !prerequisitesMet && (
                    <div className="flex flex-col items-center justify-center min-h-[420px] animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="glass-panel rounded-2xl border border-white/10 p-12 flex flex-col items-center gap-6 max-w-lg w-full text-center shadow-[0_0_60px_rgba(0,0,0,0.5)]">
                            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                                <LockClosedIcon className="h-8 w-8 text-text-secondary" />
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-white mb-2">Market Intelligence Locked</h2>
                                <p className="text-text-secondary text-sm leading-relaxed">
                                    Complete and verify all prerequisite documents to unlock this module.
                                </p>
                            </div>
                            <div className="w-full grid grid-cols-2 gap-3">
                                {(['BRD', 'PRD', 'NFR', 'UIR'] as const).map(type => {
                                    const isDone = verifiedStatuses[type]
                                    return (
                                        <div key={type} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold ${isDone ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-text-secondary'
                                            }`}>
                                            {isDone
                                                ? <CheckCircleIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                                                : <XCircleIcon className="h-4 w-4 text-text-secondary/50 shrink-0" />}
                                            {type}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}
                {/* Market Intelligence View */}
                {!selectedReqId && activeTab === 'product-intelligence' && prerequisitesMet && (() => {
                    const piNav = [
                        { id: 'overview', label: 'Overview', icon: '⚡', desc: 'Executive Summary' },
                        { id: 'market', label: 'Market', icon: '📊', desc: 'TAM / SAM / SOM' },
                        { id: 'swot', label: 'SWOT', icon: '🛡️', desc: 'Strengths & Risks' },
                        { id: 'competitors', label: 'Competitors', icon: '🔍', desc: 'Landscape' },
                        { id: 'pitchdeck', label: 'Pitch Deck', icon: '📑', desc: 'Download' },
                    ] as const

                    const isCompleted = intelligenceData?.status === 'completed'

                    return (
                        <div className="animate-in fade-in duration-300 flex flex-col" style={{ height: 'calc(100vh - 200px)', minHeight: 580 }}>

                            {/* ── Header Row ────────────────────────────── */}
                            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/5 flex-shrink-0">
                                <div>
                                    <h1 className="text-xl font-black text-white leading-tight tracking-tight">Market Intelligence</h1>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Strategy Engine</span>
                                        <div className="w-1 h-1 rounded-full bg-white/20" />
                                        <span className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">v2.4 Cluster</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {/* Show Stop button when analysis is running */}
                                    {(isPollingIntelligence || intelligenceData?.status === 'processing' || intelligenceData?.status === 'pending') ? (
                                        <button
                                            onClick={handleStopIntelligenceAnalysis}
                                            className="flex items-center gap-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(239,68,68,0.1)]"
                                        >
                                            <span className="flex h-2 w-2 relative">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                            </span>
                                            Abort Analysis
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleRunIntelligenceAnalysis}
                                            disabled={isLoadingIntelligence}
                                            className="flex items-center gap-2 bg-primary hover:bg-orange-600 disabled:opacity-20 text-white rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all border-none"
                                        >
                                            <SparklesIcon className="h-4 w-4" />
                                            {isCompleted ? 'Re-Initialize' : 'Engage Analysis'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* ── Deep Scan Message ─────────────────────────── */}
                            {showDeepScanMessage && !isCompleted && intelligenceData?.status !== 'failed' && (
                                <div className="mb-6 p-8 bg-black/40 border border-primary/20 rounded-2xl flex flex-col items-center justify-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500 relative overflow-hidden backdrop-blur-xl">
                                    <div className="absolute inset-0 bg-primary/5 blur-3xl" />
                                    <div className="relative w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20 mb-6 shadow-[0_0_30px_rgba(255,107,0,0.2)]">
                                        <SparklesIcon className="h-8 w-8 text-primary animate-pulse" />
                                    </div>
                                    <h3 className="text-xl font-black text-white mb-3 tracking-tight relative z-10">
                                        Analysis Initiated
                                    </h3>
                                    <p className="text-text-secondary max-w-lg mb-6 text-sm font-medium leading-relaxed relative z-10">
                                        Our workers are meticulously analyzing your market landscape, competitive positioning, and technical requirements. This comprehensive scan involves cross-referencing multiple global vectors.
                                    </p>
                                    <div className="inline-flex items-center gap-3 bg-primary/10 text-primary px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-primary/20 relative z-10">
                                        <ArrowPathIcon className="w-3 h-3 animate-spin shadow-[0_0_8px_rgba(255,107,0,0.5)]" />
                                        Transmission in Progress
                                    </div>
                                </div>
                            )}

                            {/* ── Horizontal Tab Strip (Only show if completed or processing) ── */}
                            {intelligenceData?.status !== 'failed' && (
                                <div className="flex items-center gap-1 mb-6 bg-black/40 border border-white/5 rounded-2xl p-1.5 flex-shrink-0 backdrop-blur-md">
                                    {piNav.map(item => {
                                        const active = piSubTab === item.id
                                        const locked = !isCompleted && item.id !== 'overview'
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => !locked && setPiSubTab(item.id)}
                                                disabled={locked}
                                                className={`flex-1 flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${active
                                                        ? 'bg-primary/20 border border-primary/30 text-primary shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                                                        : locked
                                                            ? 'opacity-20 cursor-not-allowed text-text-secondary'
                                                            : 'text-text-secondary hover:text-white hover:bg-white/5'
                                                    }`}
                                            >
                                                <span className="text-sm leading-none">{item.icon}</span>
                                                <span className="hidden sm:inline">{item.label}</span>
                                                {active && <span className="hidden md:block h-1 w-1 bg-primary rounded-full animate-pulse shadow-[0_0_5px_rgba(255,107,0,1)]" />}
                                                {locked && <span className="hidden md:inline">🔒</span>}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}


                            {/* ── Content Area ─────────────────────────── */}
                            <div className="flex-1 overflow-y-auto min-h-0">
                                {/* Loading state */}
                                {isLoadingIntelligence && (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="animate-spin h-12 w-12 border-4 border-orange-500 rounded-full border-t-transparent" />
                                    </div>
                                )}


                                {/* Not started yet — empty state */}
                                {!isLoadingIntelligence && (intelligenceData?.status === 'none' || !intelligenceData) && (
                                    <div className="flex flex-col items-center justify-center h-full text-center px-8">
                                        <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mb-5">
                                            <SparklesIcon className="h-10 w-10 text-orange-400" />
                                        </div>
                                        <h2 className="text-xl font-black text-gray-900 mb-2">Unlock Intelligence</h2>
                                        <p className="text-gray-500 max-w-sm mb-6 text-sm">Run a deep analysis to get your readiness score, market sizing, SWOT, competitive landscape, and a downloadable pitch deck.</p>
                                        <div className="grid grid-cols-2 gap-3 max-w-sm text-left mb-6">
                                            {[
                                                { icon: '📊', label: 'Market Sizing', sub: 'TAM / SAM / SOM' },
                                                { icon: '🛡', label: 'SWOT Analysis', sub: 'Strengths & Risks' },
                                                { icon: '🔍', label: 'Competitors', sub: 'Landscape view' },
                                                { icon: '📑', label: 'Pitch Deck', sub: 'Download as .docx' },
                                            ].map(f => (
                                                <div key={f.label} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                                                    <span className="text-xl">{f.icon}</span>
                                                    <p className="font-bold text-sm text-gray-900 mt-1">{f.label}</p>
                                                    <p className="text-[10px] text-gray-500">{f.sub}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={handleRunIntelligenceAnalysis} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-black px-8 py-3 rounded-xl shadow-lg shadow-orange-500/25 flex items-center gap-2">
                                            <SparklesIcon className="h-5 w-5" />
                                            Run Analysis Now
                                        </button>
                                    </div>
                                )}

                                {/* Processing state */}
                                {!isLoadingIntelligence && intelligenceData && ['pending', 'processing'].includes(intelligenceData.status) && (
                                    <div className="flex flex-col items-center justify-center h-full text-center px-8">
                                        <div className="relative w-20 h-20 mb-6">
                                            <div className="absolute inset-0 border-4 border-orange-100 rounded-full animate-ping opacity-30" />
                                            <div className="animate-spin h-20 w-20 border-4 border-orange-500 rounded-full border-t-transparent absolute inset-0" />
                                            <CpuChipIcon className="h-8 w-8 text-orange-400 opacity-60 absolute inset-0 m-auto" />
                                        </div>
                                        <h2 className="text-xl font-black text-gray-900 mb-1">Running Analysis Pipeline</h2>
                                        {intelligenceData.activity ? (
                                            <p className="text-gray-500 max-w-xs text-sm mb-4 font-medium animate-pulse">{intelligenceData.activity}</p>
                                        ) : (
                                            <p className="text-gray-500 max-w-xs text-sm mb-4">Researching market, competitors, and computing your viability score.</p>
                                        )}

                                        <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-full text-sm font-bold border border-orange-100 mb-5">
                                            <div className="h-2 w-2 bg-orange-500 rounded-full animate-pulse" />
                                            {intelligenceData.status === 'processing' ? 'Pipeline Running' : 'Job Queued — Starting...'}
                                        </div>
                                        {(intelligenceData as any).progress !== undefined && (
                                            <div className="mb-5 w-64">
                                                <div className="flex justify-between text-xs font-bold text-gray-500 mb-1.5">
                                                    <span>Progress</span>
                                                    <span>{(intelligenceData as any).progress}%</span>
                                                </div>
                                                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-gradient-to-r from-orange-400 to-rose-500 rounded-full transition-all duration-500" style={{ width: `${(intelligenceData as any).progress}%` }} />
                                                </div>
                                            </div>
                                        )}
                                        {/* Prominent Stop button inside processing card */}
                                        <button
                                            onClick={handleStopIntelligenceAnalysis}
                                            className="flex items-center gap-2 bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl px-5 py-2.5 text-sm font-black transition-all shadow-sm"
                                        >
                                            <span className="flex h-3 w-3 relative">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75" />
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                                            </span>
                                            Stop & Cancel Analysis
                                        </button>
                                        <p className="text-[11px] text-gray-400 mt-2">You can re-run it again at any time</p>
                                    </div>
                                )}

                                {/* Failed state */}
                                {!isLoadingIntelligence && intelligenceData?.status === 'failed' && (
                                    <div className="flex flex-col items-center justify-center h-full text-center px-8">
                                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-red-900 mb-2">Analysis Failed</h3>
                                        <p className="text-red-600 max-w-sm mb-5 text-sm">An error occurred during the pipeline. This is likely due to temporary model unavailability or rate limits. Please try running the analysis again.</p>
                                        <button onClick={handleRunIntelligenceAnalysis} className="flex items-center gap-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-xl px-5 py-2.5 font-bold text-sm shadow-sm transition-all focus:ring-2 focus:ring-red-500">
                                            <ArrowPathIcon className="h-4 w-4" /> Retry Analysis Now
                                        </button>
                                    </div>
                                )}

                                {/* ── COMPLETED: Sub-Tab Content ──────────────────────────── */}
                                {!isLoadingIntelligence && intelligenceData?.status === 'completed' && (

                                    <div className="h-full">


                                        {/* ══ OVERVIEW ══════════════════════════════════════════ */}
                                        {/* ══ OVERVIEW ══════════════════════════════════════════ */}
                                        {piSubTab === 'overview' && (
                                            <div className="h-full flex flex-col gap-6">
                                                <div>
                                                    <h2 className="text-lg font-black text-white tracking-tight">Executive Overview</h2>
                                                    <p className="text-xs text-text-secondary mt-1 font-medium">Core viability metrics derived from research</p>
                                                </div>

                                                {/* 4 KPI tiles */}
                                                <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
                                                    <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 overflow-hidden group/tile">
                                                        <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover/tile:opacity-100 transition-opacity" />
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-2 relative z-10">Readiness Score</p>
                                                        <div className="flex items-end gap-1 mb-3 relative z-10">
                                                            <span className="text-5xl font-black tabular-nums text-white tracking-tighter">{intelligenceData.investorReadiness?.score ?? 0}</span>
                                                            <span className="text-sm text-text-secondary/40 mb-1.5 font-bold">/100</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-2 relative z-10 border border-white/5">
                                                            <div className="h-full rounded-full bg-gradient-to-r from-primary to-orange-400 shadow-[0_0_10px_rgba(255,107,0,0.5)] transition-all duration-1000" style={{ width: `${intelligenceData.investorReadiness?.score ?? 0}%` }} />
                                                        </div>
                                                        <p className="text-[10px] font-black text-primary uppercase tracking-widest relative z-10">{intelligenceData.investorReadiness?.tier?.replace(/_/g, ' ') || 'Calculating...'}</p>
                                                    </div>

                                                    <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 overflow-hidden group/tile">
                                                        <div className="absolute inset-0 bg-info/5 opacity-0 group-hover/tile:opacity-100 transition-opacity" />
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-2 relative z-10">Survival Probability</p>
                                                        <div className="flex items-end gap-1 mb-3 relative z-10">
                                                            <span className="text-5xl font-black tabular-nums text-white tracking-tighter">{intelligenceData.startupViability?.survivalProbability ?? 0}</span>
                                                            <span className="text-sm text-info/40 mb-1.5 font-bold">%</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-2 relative z-10 border border-white/5">
                                                            <div className="h-full rounded-full bg-gradient-to-r from-info to-blue-400 shadow-[0_0_10px_rgba(14,165,233,0.5)] transition-all duration-1000" style={{ width: `${intelligenceData.startupViability?.survivalProbability ?? 0}%` }} />
                                                        </div>
                                                        <p className="text-[10px] font-black text-info uppercase tracking-widest relative z-10">{intelligenceData.startupViability?.classification || 'Analyzing Market...'}</p>
                                                    </div>

                                                    <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 overflow-hidden group/tile">
                                                        <div className="absolute inset-0 bg-purple-500/5 opacity-0 group-hover/tile:opacity-100 transition-opacity" />
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-2 relative z-10">Year 3 ARR Target</p>
                                                        <p className="text-2xl font-black text-white mb-3 mt-4 tracking-tight relative z-10">{intelligenceData.revenueProjection?.year3 || intelligenceData.revenueProjection?.year3Target || '—'}</p>
                                                        <div className="flex gap-2 flex-wrap relative z-10">
                                                            <span className="text-[9px] font-black bg-white/5 border border-white/10 text-text-secondary px-2 py-1 rounded uppercase tracking-wider">Y1: {intelligenceData.revenueProjection?.year1 || intelligenceData.revenueProjection?.year1Target || '—'}</span>
                                                            <span className="text-[9px] font-black bg-white/5 border border-white/10 text-text-secondary px-2 py-1 rounded uppercase tracking-wider">Y2: {intelligenceData.revenueProjection?.year2 || intelligenceData.revenueProjection?.year2Target || '—'}</span>
                                                        </div>
                                                    </div>

                                                    <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 overflow-hidden group/tile">
                                                        <div className="absolute inset-0 bg-green-500/5 opacity-0 group-hover/tile:opacity-100 transition-opacity" />
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-2 relative z-10">Rival Count</p>
                                                        <p className="text-5xl font-black text-white mb-3 tracking-tighter relative z-10">{(intelligenceData.competitors || []).length}</p>
                                                        <p className="text-[10px] font-black text-green-400 uppercase tracking-widest relative z-10">{(intelligenceData.competitors || []).filter((c: any) => c.type === 'direct' || c.description?.includes('direct')).length} direct threats</p>
                                                    </div>
                                                </div>

                                                {/* readiness split: strengths + gaps */}
                                                {intelligenceData.investorReadiness && (
                                                    <div className="grid grid-cols-2 gap-5 flex-1 min-h-0">
                                                        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex flex-col min-h-0 shadow-inner group">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="p-2 bg-green-500/10 rounded-xl border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.1)]"><SparklesIcon className="w-4 h-4 text-green-400" /></div>
                                                                <h3 className="font-black text-white text-[11px] uppercase tracking-widest">Strengths</h3>
                                                            </div>
                                                            <ul className="space-y-3 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                                                                {(intelligenceData.investorReadiness?.keyStrengths || intelligenceData.investorReadiness?.strengths || []).map((s: string, i: number) => (
                                                                    <li key={i} className="flex items-start gap-3 text-sm text-text-secondary leading-relaxed font-medium group/item transition-colors hover:text-white">
                                                                        <span className="flex-shrink-0 w-5 h-5 bg-green-500/10 text-green-400 rounded-lg border border-green-500/20 flex items-center justify-center text-[10px] font-black mt-0.5 shadow-inner">✓</span>
                                                                        {s}
                                                                    </li>
                                                                ))}
                                                                {!(intelligenceData.investorReadiness?.keyStrengths || intelligenceData.investorReadiness?.strengths)?.length && <li className="text-xs text-text-secondary/40 font-mono italic">NO_DATA_RETRIEVED</li>}
                                                            </ul>
                                                        </div>
                                                        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex flex-col min-h-0 shadow-inner group">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="p-2 bg-primary/10 rounded-xl border border-primary/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]"><ExclamationTriangleIcon className="w-4 h-4 text-primary" /></div>
                                                                <h3 className="font-black text-white text-[11px] uppercase tracking-widest">Gaps</h3>
                                                            </div>
                                                            <ul className="space-y-3 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                                                                {(intelligenceData.investorReadiness?.improvementRecommendations || intelligenceData.investorReadiness?.gaps || []).map((g: string, i: number) => (
                                                                    <li key={i} className="flex items-start gap-3 text-sm text-text-secondary leading-relaxed font-medium group/item transition-colors hover:text-white">
                                                                        <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary rounded-lg border border-primary/20 flex items-center justify-center text-[10px] font-black mt-0.5 shadow-inner">!</span>
                                                                        {g}
                                                                    </li>
                                                                ))}
                                                                {!(intelligenceData.investorReadiness?.improvementRecommendations || intelligenceData.investorReadiness?.gaps)?.length && <li className="text-xs text-green-400 font-mono italic">ALL_GAPS_STABILIZED</li>}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ══ MARKET ════════════════════════════════════════════ */}
                                        {/* ══ MARKET ════════════════════════════════════════════ */}
                                        {piSubTab === 'market' && (
                                            <div className="h-full flex flex-col gap-6">
                                                <div>
                                                    <h2 className="text-lg font-black text-white tracking-tight">Market Stratification</h2>
                                                    <p className="text-xs text-text-secondary mt-1 font-medium">Sizing and sector trajectory metrics</p>
                                                </div>

                                                {/* TAM / SAM / SOM tiles */}
                                                <div className="grid grid-cols-3 gap-5">
                                                    {[
                                                        { label: 'Total Addressable Market', short: 'TAM', value: intelligenceData.marketTrends?.tamSize || intelligenceData.marketTrends?.marketSize || '—', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
                                                        { label: 'Serviceable Addressable Market', short: 'SAM', value: intelligenceData.marketTrends?.samSize || intelligenceData.marketTrends?.marketSize || '—', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
                                                        { label: 'Serviceable Obtainable Market', short: 'SOM', value: intelligenceData.marketTrends?.somSize || intelligenceData.marketTrends?.marketSize || '—', color: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
                                                    ].map(m => (
                                                        <div key={m.short} className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-inner flex flex-col gap-3 group">
                                                            <div className={`w-10 h-10 rounded-xl ${m.bg} border ${m.border} flex items-center justify-center ${m.color} text-[10px] font-black tracking-widest`}>{m.short}</div>
                                                            <div className="text-2xl font-black text-white leading-tight tracking-tight">{m.value}</div>
                                                            <p className="text-[10px] text-text-secondary font-black uppercase tracking-widest">{m.label}</p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Trends */}
                                                <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-inner flex-1 min-h-0 flex flex-col group">
                                                    <div className="flex items-center gap-3 mb-6">
                                                        <div className="p-2 bg-primary/10 rounded-xl border border-primary/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]"><ChartPieIcon className="w-4 h-4 text-primary" /></div>
                                                        <h3 className="font-black text-white text-[11px] uppercase tracking-widest">Accelerators</h3>
                                                        <span className="ml-auto text-[9px] font-black bg-white/5 border border-white/10 text-white/50 px-2.5 py-1 rounded-full uppercase tracking-wider">{(intelligenceData.marketTrends?.trends || []).length} Verified Vectors</span>
                                                    </div>
                                                    <ul className="space-y-4 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                                                        {(intelligenceData.marketTrends?.trends || []).map((trend: any, i: number) => (
                                                            <li key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group/trend">
                                                                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-black/40 border border-white/10 text-primary text-[11px] font-black flex items-center justify-center transition-transform group-hover/trend:scale-110">{i + 1}</span>
                                                                <div className="flex flex-col gap-1">
                                                                    <span className="text-sm text-text-secondary font-medium group-hover/trend:text-white transition-colors">
                                                                        {typeof trend === 'object' ? (trend.description || trend.trend || JSON.stringify(trend)) : trend}
                                                                    </span>
                                                                    {typeof trend === 'object' && trend.data_point && (
                                                                        <span className="text-[10px] font-black text-primary uppercase tracking-[0.1em] mt-1">{trend.data_point}</span>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                        {!intelligenceData.marketTrends?.trends?.length && (
                                                            <li className="text-xs text-text-secondary/40 font-mono italic text-center py-12">NO_DATA_BUFFERED</li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {/* ══ SWOT ══════════════════════════════════════════════ */}
                                        {piSubTab === 'swot' && (
                                            <div className="h-full flex flex-col gap-6">
                                                <div>
                                                    <h2 className="text-lg font-black text-white tracking-tight">Threat Matrix Analysis</h2>
                                                    <p className="text-xs text-text-secondary mt-1 font-medium">Internal and external strategic positioning</p>
                                                </div>

                                                <div className="grid grid-cols-2 gap-5 flex-1 min-h-0">
                                                    {([
                                                        { key: 'strengths', label: 'Strengths', icon: '🛡️', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
                                                        { key: 'weaknesses', label: 'Weaknesses', icon: '⚠️', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
                                                        { key: 'opportunities', label: 'Opportunities', icon: '🚀', color: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
                                                        { key: 'threats', label: 'Threats', icon: '🔥', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
                                                    ] as const).map(quad => (
                                                        <div key={quad.key} className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex flex-col min-h-0 shadow-inner group">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className={`p-2 ${quad.bg} rounded-xl border ${quad.border} shadow-[0_0_15px_rgba(0,0,0,0.3)]`}><span className="text-sm">{quad.icon}</span></div>
                                                                <h3 className={`font-black ${quad.color} text-[11px] uppercase tracking-widest`}>{quad.label}</h3>
                                                                <span className="ml-auto text-[9px] font-black text-white/20 group-hover:text-white/40 transition-colors">{(intelligenceData.swot?.[quad.key as keyof typeof intelligenceData.swot] || []).length} Points</span>
                                                            </div>
                                                            <ul className="space-y-3 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                                                                {(intelligenceData.swot?.[quad.key as keyof typeof intelligenceData.swot] || []).map((item: string, i: number) => (
                                                                    <li key={i} className="flex items-start gap-4 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group/swot">
                                                                        <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${quad.bg.replace('/10', '/50')} mt-1.5 group-hover/swot:scale-125 transition-transform`} />
                                                                        <span className="text-sm text-text-secondary font-medium group-hover/swot:text-white transition-colors leading-relaxed">{item}</span>
                                                                    </li>
                                                                ))}
                                                                {!(intelligenceData.swot?.[quad.key as keyof typeof intelligenceData.swot]?.length) && <li className="text-xs text-text-secondary/40 font-mono italic text-center py-8">NO_DATA_BUFFERED</li>}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ══ COMPETITORS ════════════════════════════════════════ */}
                                        {piSubTab === 'competitors' && (
                                            <div className="h-full flex flex-col gap-6">
                                                <div>
                                                    <h2 className="text-lg font-black text-white tracking-tight">Market Adversaries</h2>
                                                    <p className="text-xs text-text-secondary mt-1 font-medium">Direct and indirect competitive landscape mapping</p>
                                                </div>

                                                <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-inner flex-1 min-h-0 flex flex-col relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
                                                    <div className="overflow-y-auto flex-1 custom-scrollbar relative z-10">
                                                        <table className="w-full text-left border-collapse">
                                                            <thead className="bg-white/[0.02] sticky top-0 z-20 backdrop-blur-md border-b border-white/5">
                                                                <tr>
                                                                    <th className="py-4 px-6 text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Designation</th>
                                                                    <th className="py-4 px-6 text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Threat Level</th>
                                                                    <th className="py-4 px-6 text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Monetization</th>
                                                                    <th className="py-4 px-6 text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Advantage</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-white/5">
                                                                {(intelligenceData.competitors || []).map((comp: any, index: number) => (
                                                                    <tr key={index} className="group/row hover:bg-white/[0.03] transition-colors">
                                                                        <td className="py-5 px-6">
                                                                            <div className="flex items-center gap-4">
                                                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center text-white text-xs font-black group-hover/row:border-primary/30 group-hover/row:text-primary transition-all shadow-lg">
                                                                                    {comp.name?.charAt(0) || '?'}
                                                                                </div>
                                                                                <span className="font-black text-[13px] text-white tracking-tight group-hover/row:text-primary transition-colors">{comp.name}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-5 px-6">
                                                                            <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border ${comp.type === 'direct' || comp.description?.toLowerCase().includes('direct')
                                                                                    ? 'bg-red-500/10 text-red-400 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.1)]'
                                                                                    : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                                                }`}>
                                                                                {comp.type === 'direct' || comp.description?.toLowerCase().includes('direct') ? 'Critical' : 'Moderate'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-5 px-6 text-xs text-text-secondary font-medium tracking-tight whitespace-nowrap">{comp.pricingModel || (comp.pricingTiers && comp.pricingTiers[0]) || 'SCANNING...'}</td>
                                                                        <td className="py-5 px-6 text-xs text-text-secondary font-medium leading-relaxed max-w-xs group-hover/row:text-white transition-colors">
                                                                            {comp.ourAdvantage || comp.description || 'IDENTIFYING_GAPS...'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {(!intelligenceData.competitors || intelligenceData.competitors.length === 0) && (
                                                                    <tr>
                                                                        <td colSpan={4} className="text-center py-20 text-[10px] font-black text-text-secondary/40 uppercase tracking-[0.3em] font-mono italic">
                                                                            NO_ADVERSARIES_DETECTED
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ══ PITCH DECK ═════════════════════════════════════════ */}
                                        {piSubTab === 'pitchdeck' && (
                                            <div className="h-full flex flex-col gap-6">
                                                <div>
                                                    <h2 className="text-lg font-black text-white tracking-tight">Investor Deck Synthesis</h2>
                                                    <p className="text-xs text-text-secondary mt-1 font-medium">Ready-to-deploy capital acquisition assets</p>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
                                                    {/* Primary Download Card */}
                                                    <div className="bg-gradient-to-br from-orange-600 to-rose-600 rounded-3xl p-8 text-white shadow-[0_20px_50px_rgba(255,107,0,0.3)] flex flex-col justify-between relative overflow-hidden group/pitch">
                                                        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover/pitch:scale-125 transition-transform duration-1000" />
                                                        <div className="relative z-10">
                                                            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/20 shadow-xl group-hover/pitch:rotate-6 transition-transform">
                                                                <PresentationChartBarIcon className="w-8 h-8 text-white" />
                                                            </div>
                                                            <h3 className="text-3xl font-black mb-3 tracking-tight">Deck Finalized</h3>
                                                            <p className="text-sm text-orange-100 leading-relaxed mb-8 opacity-90 font-medium font-heading">
                                                                Synthesized from your requirements and market vectors. Optimized for VC narratives.
                                                            </p>
                                                            <ul className="space-y-3 mb-8">
                                                                {[
                                                                    'Vision & Value Prop',
                                                                    'TAM/SAM/SOM Stratification',
                                                                    'Competitive Matrix & SWOT',
                                                                    'Growth Roadmap'
                                                                ].map(slide => (
                                                                    <li key={slide} className="flex items-center gap-3 text-[11px] font-black uppercase tracking-widest text-white/80">
                                                                        <CheckCircleIcon className="w-4 h-4 text-white" />
                                                                        {slide}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                const r = import.meta.env.VITE_API_URL || 'http://localhost:3334';
                                                                const host = r.endsWith('/api') ? r.slice(0, -4) : r;
                                                                window.open(`${host}/api/product-intelligence/${id}/pitch-deck/download?token=${localStorage.getItem('token')}`, '_blank');
                                                            }}
                                                            className="relative z-10 flex items-center justify-center gap-3 bg-white text-orange-600 hover:bg-orange-50 transition-all rounded-2xl px-6 py-4 font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl hover:-translate-y-1 active:translate-y-0"
                                                        >
                                                            <ArrowDownTrayIcon className="w-5 h-5" />
                                                            Initialize Export (.docx)
                                                        </button>
                                                    </div>

                                                    {/* Secondary Metadata/Score Card */}
                                                    <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-inner flex flex-col min-h-0 group">
                                                        <div className="flex items-center gap-3 mb-8">
                                                            <div className="p-2 bg-info/10 rounded-xl border border-info/20 shadow-[0_0_15px_rgba(14,165,233,0.1)]"><SparklesIcon className="w-5 h-5 text-info" /></div>
                                                            <h3 className="font-black text-white text-[11px] uppercase tracking-widest">Synthesis Breakdown</h3>
                                                            <span className="ml-auto text-[9px] font-mono text-white/20">V2.4</span>
                                                        </div>

                                                        <div className="space-y-6 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                                                            <div className="p-5 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                                                                <p className="text-[10px] font-black text-text-secondary/50 uppercase tracking-widest mb-3">Readiness Score</p>
                                                                <div className="flex items-end gap-4">
                                                                    <span className="text-5xl font-black text-white leading-none tracking-tighter">
                                                                        {intelligenceData.investorReadiness?.score ?? '??'}
                                                                        <span className="text-sm text-text-secondary ml-1 opacity-40">/100</span>
                                                                    </span>
                                                                    <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border mb-1 ${(intelligenceData.investorReadiness?.score || 0) > 70
                                                                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                                                            : 'bg-primary/10 text-primary border-primary/20'
                                                                        }`}>
                                                                        {intelligenceData.investorReadiness?.tier?.replace(/_/g, ' ') || 'SCANNING...'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="p-5 bg-white/5 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                                                                <p className="text-[10px] font-black text-text-secondary/50 uppercase tracking-widest mb-4">Projected Trajectory (MRR)</p>
                                                                <div className="grid grid-cols-3 gap-3">
                                                                    {[
                                                                        { l: 'Year 01', v: intelligenceData.revenueProjection?.year1 || intelligenceData.revenueProjection?.year1Target },
                                                                        { l: 'Year 02', v: intelligenceData.revenueProjection?.year2 || intelligenceData.revenueProjection?.year2Target },
                                                                        { l: 'Year 03', v: intelligenceData.revenueProjection?.year3 || intelligenceData.revenueProjection?.year3Target },
                                                                    ].map((y, i) => (
                                                                        <div key={i} className="bg-black/40 rounded-xl p-3 border border-white/5 text-center">
                                                                            <p className="text-[8px] font-black text-text-secondary/60 uppercase mb-1">{y.l}</p>
                                                                            <p className="text-xs font-black text-white truncate">{y.v || '—'}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            <div className="p-5 bg-primary/5 rounded-2xl border border-primary/10">
                                                                <p className="text-[10px] font-black text-primary uppercase tracking-widest mb-3 flex items-center gap-2">
                                                                    <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                                                                    Strategic Edge
                                                                </p>
                                                                <ul className="space-y-2">
                                                                    {(intelligenceData.investorReadiness?.keyStrengths || intelligenceData.swot?.strengths || []).slice(0, 3).map((s: string, i: number) => (
                                                                        <li key={i} className="text-xs text-text-secondary/90 font-medium flex items-start gap-2">
                                                                            <span className="text-primary mt-0.5">▸</span>{s}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })()}



                {!selectedReqId && activeTab === 'project' && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300 relative z-10">
                        <div>
                            <h1 className="text-3xl font-black text-white mb-1 flex items-center gap-2 tracking-tight">
                                Project Information
                                <CommandLineIcon className="h-5 w-5 text-primary" />
                            </h1>
                            <div className="flex justify-between items-end mt-2">
                                <p className="text-sm font-medium text-text-secondary">Global overview and context of the core project architecture.</p>
                                <div className="flex items-center gap-3">
                                    {!prerequisitesMet && (
                                        <Button
                                            variant={isEditingCore ? "destructive" : "outline"}
                                            size="sm"
                                            className="flex items-center gap-2 border-primary/30 text-primary hover:bg-primary hover:text-white transition-all -mb-4 bg-primary/10 font-bold uppercase tracking-widest text-[10px]"
                                            onClick={() => setIsEditingCore(!isEditingCore)}
                                        >
                                            <PencilSquareIcon className="h-4 w-4" />
                                            {isEditingCore ? 'Cancel Edit' : 'Edit Core'}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-2 border-primary/30 text-primary hover:bg-primary hover:text-white transition-all -mb-4 bg-primary/10 font-bold uppercase tracking-widest text-[10px]"
                                        onClick={() => triggerDownload(handleDownloadFullProject)}
                                        disabled={isExporting}
                                    >
                                        <ArrowDownTrayIcon className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
                                        Download Full Documentation (.zip)
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            <div className="glass-panel p-6 border border-white/10 hover:border-primary/30 transition-all rounded-2xl">
                                <div className="grid grid-cols-[160px_1fr] gap-4">
                                    <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest pt-1 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary" /> Title
                                    </span>
                                    <span className="text-lg text-white font-bold">{project.name}</span>
                                </div>
                            </div>

                            <div className="glass-panel p-6 border border-white/10 hover:border-primary/30 transition-all rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 blur-3xl" />
                                <div className="grid grid-cols-[160px_1fr] gap-4 relative z-10">
                                    <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest pt-1 flex items-start gap-2 mt-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-info mt-1" /> Description
                                    </span>
                                    {isEditingCore ? (
                                        <textarea
                                            value={editDesc}
                                            onChange={(e) => setEditDesc(e.target.value)}
                                            rows={5}
                                            className="w-full p-4 text-sm text-white bg-black/40 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none font-medium leading-relaxed"
                                            placeholder="Enter project description..."
                                        />
                                    ) : (
                                        <span className="text-sm text-text-secondary font-medium leading-relaxed">
                                            {project.description || 'No description provided.'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="glass-panel p-6 border border-white/10 hover:border-primary/30 transition-all rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-full bg-primary/50" />
                                <div className="grid grid-cols-[160px_1fr] gap-4 relative z-10">
                                    <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest pt-1 flex items-start gap-2 mt-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1" /> Tech Stack
                                    </span>
                                    {isEditingCore ? (
                                        <textarea
                                            value={editTech}
                                            onChange={(e) => setEditTech(e.target.value)}
                                            rows={3}
                                            className="w-full p-4 text-sm text-white bg-black/40 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none font-medium leading-relaxed"
                                            placeholder="Enter technical details/tech stack..."
                                        />
                                    ) : (
                                        <span className="text-sm text-white font-medium leading-relaxed bg-black/40 p-4 rounded-xl border border-white/5 shadow-inner">
                                            {project.technicalDetails || 'Standard implementation stack.'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {isEditingCore && (
                                <>
                                    {/* AI Generation Parameters Section */}
                                    <div className="glass-panel p-6 border border-white/10 hover:border-primary/30 transition-all rounded-2xl relative overflow-hidden bg-[#131B2C]/40 backdrop-blur-xl">
                                        <div className="mb-6 flex items-center gap-4">
                                            <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                                                <BoltIcon className="w-6 h-6 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2">
                                                    AI Generation Parameters
                                                </h3>
                                                <p className="text-xs text-text-secondary font-medium mt-0.5">Control the density and complexity of AI-generated documentation.</p>
                                            </div>
                                        </div>
                                        <div className="space-y-6">
                                            {[
                                                { key: 'brd', label: 'Business Requirements', desc: 'Threshold for core business logic units' },
                                                { key: 'prd', label: 'Product Requirements', desc: 'Threshold for functional specification units' },
                                                { key: 'uir', label: 'UI Specifications', desc: 'Threshold for visual interface components' },
                                                { key: 'nfr', label: 'Non-Functional Constraints', desc: 'Threshold for security and performance units' },
                                            ].map((item) => (
                                                <div key={item.key} className="flex items-center space-x-8">
                                                    <div className="w-1/3">
                                                        <label className="block text-xs font-black text-white uppercase tracking-[0.1em]">{item.label}</label>
                                                        <p className="text-[10px] text-text-secondary font-bold uppercase tracking-wider mt-1 opacity-60">{item.desc}</p>
                                                    </div>

                                                    <div className="flex items-center space-x-6 flex-1">
                                                        <Switch
                                                            checked={preferences[item.key]?.enabled ?? true}
                                                            onChange={(val) => updatePreference(item.key as any, 'enabled', val)}
                                                        />
                                                        <div className="flex-1 px-2">
                                                            <Slider
                                                                value={preferences[item.key]?.value ?? 15}
                                                                min={0}
                                                                max={30}
                                                                onChange={(val) => updatePreference(item.key as any, 'value', val)}
                                                                disabled={!(preferences[item.key]?.enabled ?? true)}
                                                            />
                                                        </div>
                                                        <span className="text-sm font-black text-primary w-10 text-right font-mono">
                                                            {(preferences[item.key]?.value ?? 15).toString().padStart(2, '0')}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Button: Regenerate */}
                                    <div className="flex justify-end gap-3 mt-4">
                                        <Button
                                            variant="outline"
                                            onClick={() => setIsEditingCore(false)}
                                            disabled={isRegenerating}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="default"
                                            onClick={handleSaveAndRegenerate}
                                            disabled={isRegenerating}
                                            className="bg-primary hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-xs px-6 shadow-[0_0_15px_rgba(255,107,0,0.3)]"
                                        >
                                            {isRegenerating ? (
                                                <span className="flex items-center gap-2">
                                                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                                    Regenerating...
                                                </span>
                                            ) : 'Save & Regenerate'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {!selectedReqId && activeTab === 'test-cases' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 relative z-10">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h1 className="text-3xl font-black text-white flex items-center gap-2">
                                    Test Cases <div className="p-1.5 bg-white/5 rounded-full border border-white/10"><InformationCircleIcon className="h-5 w-5 text-text-secondary" /></div>
                                </h1>
                                <div className="flex items-center gap-3 mt-3">
                                    <span className="text-[10px] font-black tracking-widest uppercase text-text-secondary">User Stories</span>
                                    <span className="bg-primary/20 text-primary px-2.5 py-0.5 rounded text-[10px] font-bold border border-primary/30">{storiesWithTestCounts.length}</span>
                                </div>
                                <p className="text-sm font-medium text-text-secondary mt-3 max-w-2xl">
                                    Each user story contains its own test cases. View or create test cases by choosing a user story.
                                </p>
                            </div>
                        </div>

                        {/* PRD Selector & Search */}
                        <div className="flex flex-col md:flex-row gap-4 mb-8">
                            <div className="relative flex-1 group">
                                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-text-secondary group-focus-within:text-primary transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Search user stories..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all shadow-inner text-white placeholder-text-secondary/50 text-sm font-medium"
                                />
                            </div>
                            <div className="w-full md:w-96">
                                <select
                                    value={testCaseViewPrdId || ''}
                                    onChange={(e) => setTestCaseViewPrdId(e.target.value)}
                                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 text-white font-medium text-sm shadow-inner cursor-pointer appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%239CA3AF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px_12px] bg-[right_16px_center]"
                                >
                                    <option value="" disabled className="bg-card text-text-secondary">Select a PRD</option>
                                    {project?.requirements?.filter(r => r.type === 'PRD').map(prd => (
                                        <option key={prd.id} value={prd.id} className="bg-card text-white py-2">{prd.title}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                            <div className="glass-panel p-5 rounded-2xl border border-white/10 flex items-center gap-4 hover:border-blue-500/30 transition-all group overflow-hidden relative">
                                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="bg-blue-500/10 p-3 rounded-xl border border-blue-500/20 relative z-10">
                                    <DocumentTextIcon className="h-6 w-6 text-blue-400" />
                                </div>
                                <div className="relative z-10">
                                    <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1">Total Stories</p>
                                    <h3 className="text-2xl font-black text-white">{storiesWithTestCounts.length}</h3>
                                </div>
                            </div>
                            <div className="glass-panel p-5 rounded-2xl border border-white/10 flex items-center gap-4 hover:border-yellow-500/30 transition-all group overflow-hidden relative">
                                <div className="absolute inset-0 bg-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="bg-yellow-500/10 p-3 rounded-xl border border-yellow-500/20 relative z-10">
                                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-yellow-500" />
                                </div>
                                <div className="relative z-10">
                                    <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1">Stories w/ Tests</p>
                                    <h3 className="text-2xl font-black text-white">{storiesWithTestCounts.filter(s => s.count > 0).length}</h3>
                                </div>
                            </div>
                            <div className="glass-panel p-5 rounded-2xl border border-white/10 flex items-center gap-4 hover:border-green-500/30 transition-all group overflow-hidden relative">
                                <div className="absolute inset-0 bg-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="bg-green-500/10 p-3 rounded-xl border border-green-500/20 relative z-10">
                                    <BeakerIcon className="h-6 w-6 text-green-400" />
                                </div>
                                <div className="relative z-10">
                                    <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1">Total Cases</p>
                                    <h3 className="text-2xl font-black text-white">{storiesWithTestCounts.reduce((acc, s) => acc + s.count, 0)}</h3>
                                </div>
                            </div>
                        </div>

                        {/* Stories List */}
                        <div className="space-y-4">
                            {isLoadingStories ? (
                                <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div></div>
                            ) : storiesWithTestCounts.length === 0 ? (
                                <div className="text-center py-16 bg-black/20 rounded-2xl border border-dashed border-white/10">
                                    <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><DocumentTextIcon className="h-8 w-8 text-text-secondary" /></div>
                                    <p className="text-text-secondary font-medium">No user stories found for this PRD.</p>
                                    <Button
                                        variant="outline"
                                        className="mt-6 bg-primary/10 border border-primary/20 text-primary hover:bg-primary hover:text-white transition-colors"
                                        onClick={() => {
                                            const prdName = project?.requirements?.find(r => r.id === testCaseViewPrdId)?.title;
                                            toast.success(`Switch to PRD tab -> Select ${prdName} -> Generate User Stories`);
                                        }}
                                    >
                                        Go to PRD to Generate Stories
                                    </Button>
                                </div>
                            ) : (
                                storiesWithTestCounts
                                    .filter(item => item.story.title.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map((item, index) => (
                                        <div key={item.story.id} className="glass-panel p-6 rounded-2xl border border-white/10 hover:border-primary/40 hover:shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:-translate-y-0.5 transition-all group relative overflow-hidden">
                                            <div className="absolute left-0 top-0 w-1 h-full bg-white/10 group-hover:bg-primary transition-colors"></div>
                                            <div className="pl-4">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest bg-white/5 px-2 py-0.5 rounded border border-white/10">US{String(index + 1).padStart(2, '0')}</span>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-colors border border-transparent hover:border-primary/20"
                                                        onClick={() => navigate(`/user-stories/${item.story.id}/test-cases`)}
                                                    >
                                                        <ArrowRightIcon className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary transition-colors cursor-pointer tracking-tight" onClick={() => navigate(`/user-stories/${item.story.id}/test-cases`)}>
                                                    {item.story.title}
                                                </h3>
                                                <p className="text-sm text-text-secondary font-medium leading-relaxed line-clamp-2 mb-6 max-w-4xl">{item.story.description}</p>

                                                <div className="flex items-center justify-between border-t border-white/5 pt-4">
                                                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${item.count > 0 ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                                        }`}>
                                                        {item.count > 0 ? (
                                                            <CheckCircleIcon className="h-3.5 w-3.5" />
                                                        ) : (
                                                            <XCircleIcon className="h-3.5 w-3.5" />
                                                        )}
                                                        {item.count} cases
                                                    </div>

                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-primary hover:bg-primary/10 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-primary/20 hover:border-primary/40 rounded-lg"
                                                        onClick={() => navigate(`/user-stories/${item.story.id}/test-cases`)}
                                                    >
                                                        {item.count > 0 ? 'View Tests' : 'Gen Tests'} <ArrowRightIcon className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                    </div>
                )}

                {/* Beta Placeholders for Strategic Initiatives & Integrations */}
                {!selectedReqId && activeTab === 'strategic-initiatives' && (
                    <div className="flex items-center justify-center min-h-[400px] glass-panel rounded-2xl border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] p-8 animate-in fade-in slide-in-from-bottom-2 duration-300 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
                        <div className="text-center relative z-10">
                            <ChartBarIcon className="h-16 w-16 text-primary/30 mx-auto mb-4 animate-pulse-subtle" />
                            <h3 className="text-2xl font-black text-white mb-2 tracking-tight">
                                Strategic Initiatives
                            </h3>
                            <div className="inline-flex items-center gap-2 mb-4">
                                <span className="bg-primary/20 text-primary px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-primary/30 shadow-[0_0_10px_rgba(255,107,0,0.2)]">Beta Engine</span>
                            </div>
                            <p className="text-text-secondary max-w-md mx-auto leading-relaxed font-medium">
                                We're deploying an advanced module to dynamically align your product features with business KPIs and OKRs. Stay tuned for early access.
                            </p>
                        </div>
                    </div>
                )}

                {!selectedReqId && activeTab === 'integrations' && id && (
                    <div className="glass-panel rounded-2xl border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <IntegrationsPage projectId={id} />
                    </div>
                )}

                {!selectedReqId && activeTab === 'bp' && project && !prerequisitesMet && (
                    <div className="flex flex-col items-center justify-center min-h-[420px] animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="glass-panel rounded-2xl border border-white/10 p-12 flex flex-col items-center gap-6 max-w-lg w-full text-center shadow-[0_0_60px_rgba(0,0,0,0.5)]">
                            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                                <LockClosedIcon className="h-8 w-8 text-text-secondary" />
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-white mb-2">Business Process Locked</h2>
                                <p className="text-text-secondary text-sm leading-relaxed">
                                    Complete and verify all prerequisite documents to unlock this module.
                                </p>
                            </div>
                            <div className="w-full grid grid-cols-2 gap-3">
                                {(['BRD', 'PRD', 'NFR', 'UIR'] as const).map(type => {
                                    const isDone = verifiedStatuses[type]
                                    return (
                                        <div key={type} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold ${isDone ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-text-secondary'
                                            }`}>
                                            {isDone
                                                ? <CheckCircleIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                                                : <XCircleIcon className="h-4 w-4 text-text-secondary/50 shrink-0" />}
                                            {type}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {!selectedReqId && activeTab === 'bp' && project && prerequisitesMet && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 relative z-10 space-y-6">
                        {/* Header Section */}
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <h1 className="text-3xl font-black text-white whitespace-nowrap tracking-tight">
                                    Business Process Workflow
                                </h1>
                                <div className="p-1.5 bg-white/5 rounded-full border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                                    <CommandLineIcon className="h-5 w-5 text-text-secondary" />
                                </div>
                            </div>
                            {project.businessProcess?.mermaidCode && (
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-info/10 border-info/30 text-info hover:bg-info/20 hover:text-white"
                                        onClick={handleGenerateBusinessProcess}
                                        disabled={isGeneratingBp}
                                    >
                                        <ArrowPathIcon className={`h-4 w-4 ${isGeneratingBp ? 'animate-spin' : ''}`} />
                                        Regenerate Process
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-white/5 border-white/10 text-white hover:bg-white/10"
                                        onClick={() => {
                                            const svg = document.querySelector(".mermaid svg");
                                            if (!svg) {
                                                toast.error("Flowchart SVG not loaded yet");
                                                return;
                                            }
                                            const svgString = new XMLSerializer().serializeToString(svg);
                                            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                                            const url = URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `${project.name}_business_process.svg`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            URL.revokeObjectURL(url);
                                            toast.success("SVG Exported successfully!");
                                        }}
                                    >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                        Export SVG
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-white/5 border-white/10 text-white hover:bg-white/10"
                                        onClick={() => {
                                            const svg = document.querySelector(".mermaid svg") as SVGSVGElement;
                                            if (!svg) {
                                                toast.error("Flowchart SVG not loaded yet");
                                                return;
                                            }
                                            try {
                                                let width = 800;
                                                let height = 600;
                                                const viewBox = svg.getAttribute("viewBox");
                                                if (viewBox) {
                                                    const parts = viewBox.split(" ");
                                                    if (parts.length === 4) {
                                                        width = parseFloat(parts[2]) || 800;
                                                        height = parseFloat(parts[3]) || 600;
                                                    }
                                                } else {
                                                    const svgWidth = svg.getAttribute("width");
                                                    const svgHeight = svg.getAttribute("height");
                                                    if (svgWidth && svgHeight) {
                                                        width = parseFloat(svgWidth) || 800;
                                                        height = parseFloat(svgHeight) || 600;
                                                    }
                                                }

                                                // Clone SVG and set explicit width/height dimensions from viewBox for correct canvas rendering
                                                const svgClone = svg.cloneNode(true) as SVGSVGElement;
                                                svgClone.setAttribute("width", width.toString());
                                                svgClone.setAttribute("height", height.toString());
                                                svgClone.style.width = width + "px";
                                                svgClone.style.height = height + "px";
                                                svgClone.style.maxWidth = "none";
                                                svgClone.style.maxHeight = "none";

                                                let svgString = new XMLSerializer().serializeToString(svgClone);
                                                if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
                                                    svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
                                                }
                                                const base64Svg = window.btoa(unescape(encodeURIComponent(svgString)));
                                                const dataUrl = `data:image/svg+xml;base64,${base64Svg}`;
                                                const image = new Image();
                                                image.onload = () => {
                                                    const canvas = document.createElement('canvas');
                                                    canvas.width = width * 2;
                                                    canvas.height = height * 2;
                                                    const context = canvas.getContext('2d');
                                                    if (context) {
                                                        context.fillStyle = '#0b0f19';
                                                        context.fillRect(0, 0, canvas.width, canvas.height);
                                                        context.drawImage(image, 0, 0, canvas.width, canvas.height);
                                                        const pngURL = canvas.toDataURL('image/png');
                                                        const link = document.createElement('a');
                                                        link.href = pngURL;
                                                        link.download = `${project.name}_business_process.png`;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                        toast.success("PNG Exported successfully!");
                                                    }
                                                };
                                                image.src = dataUrl;
                                            } catch (err) {
                                                console.error("Export PNG failed:", err);
                                                toast.error("Export PNG failed");
                                            }
                                        }}
                                    >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                        Export PNG
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-white/5 border-white/10 text-white hover:bg-white/10"
                                        onClick={() => {
                                            const svg = document.querySelector(".mermaid svg") as SVGSVGElement;
                                            if (!svg) {
                                                toast.error("Flowchart SVG not loaded yet");
                                                return;
                                            }
                                            try {
                                                let width = 800;
                                                let height = 600;
                                                const viewBox = svg.getAttribute("viewBox");
                                                if (viewBox) {
                                                    const parts = viewBox.split(" ");
                                                    if (parts.length === 4) {
                                                        width = parseFloat(parts[2]) || 800;
                                                        height = parseFloat(parts[3]) || 600;
                                                    }
                                                } else {
                                                    const svgWidth = svg.getAttribute("width");
                                                    const svgHeight = svg.getAttribute("height");
                                                    if (svgWidth && svgHeight) {
                                                        width = parseFloat(svgWidth) || 800;
                                                        height = parseFloat(svgHeight) || 600;
                                                    }
                                                }

                                                // Clone SVG and set explicit width/height dimensions from viewBox for correct canvas rendering
                                                const svgClone = svg.cloneNode(true) as SVGSVGElement;
                                                svgClone.setAttribute("width", width.toString());
                                                svgClone.setAttribute("height", height.toString());
                                                svgClone.style.width = width + "px";
                                                svgClone.style.height = height + "px";
                                                svgClone.style.maxWidth = "none";
                                                svgClone.style.maxHeight = "none";

                                                let svgString = new XMLSerializer().serializeToString(svgClone);
                                                if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
                                                    svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
                                                }
                                                const base64Svg = window.btoa(unescape(encodeURIComponent(svgString)));
                                                const dataUrl = `data:image/svg+xml;base64,${base64Svg}`;
                                                const image = new Image();
                                                image.onload = () => {
                                                    const canvas = document.createElement('canvas');
                                                    canvas.width = width * 2;
                                                    canvas.height = height * 2;
                                                    const context = canvas.getContext('2d');
                                                    if (context) {
                                                        context.fillStyle = '#0b0f19';
                                                        context.fillRect(0, 0, canvas.width, canvas.height);
                                                        context.drawImage(image, 0, 0, canvas.width, canvas.height);
                                                        const imgData = canvas.toDataURL('image/png');
                                                        const isLandscape = width > height;
                                                        const pdf = new jsPDF({
                                                            orientation: isLandscape ? 'l' : 'p',
                                                            unit: 'mm',
                                                            format: 'a4'
                                                        });
                                                        const pageWidth = pdf.internal.pageSize.getWidth();
                                                        const pageHeight = pdf.internal.pageSize.getHeight();

                                                        // Draw matching dark background (#0b0f19) to fill the entire A4 page
                                                        pdf.setFillColor(11, 15, 25);
                                                        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

                                                        // Scale and center the diagram within 15mm margins
                                                        const margin = 15;
                                                        const maxWidth = pageWidth - (margin * 2);
                                                        const maxHeight = pageHeight - (margin * 2);

                                                        const aspectRatio = width / height;
                                                        let imgWidth = maxWidth;
                                                        let imgHeight = imgWidth / aspectRatio;

                                                        if (imgHeight > maxHeight) {
                                                            imgHeight = maxHeight;
                                                            imgWidth = imgHeight * aspectRatio;
                                                        }

                                                        const x = (pageWidth - imgWidth) / 2;
                                                        const y = (pageHeight - imgHeight) / 2;

                                                        pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
                                                        pdf.save(`${project.name}_business_process.pdf`);
                                                        toast.success("PDF Exported successfully!");
                                                    }
                                                };
                                                image.src = dataUrl;
                                            } catch (err) {
                                                console.error("Export PDF failed:", err);
                                                toast.error("Export PDF failed");
                                            }
                                        }}
                                    >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                        Export PDF
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Main Content Area */}
                        {!project.businessProcess?.mermaidCode ? (
                            <div className="flex flex-col items-center justify-center min-h-[450px] text-center p-8 bg-black/20 rounded-2xl border-2 border-dashed border-white/10 relative overflow-hidden group">
                                <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-10 transition-opacity" />
                                <div className="mb-8 relative rounded-full p-4 bg-white/5 border border-white/10 shadow-[0_0_30px_rgba(255,107,0,0.1)] animate-pulse-subtle">
                                    <CommandLineIcon className="h-16 w-16 text-primary/70" />
                                </div>
                                <h2 className="text-2xl font-black text-white mb-3 tracking-tight">
                                    Generate Business Process Flowchart
                                </h2>
                                <p className="text-text-secondary max-w-md mb-8 font-medium">
                                    DocSprint analyzes your Business and Product Requirements to automatically generate a visual business process flow, helping teams understand workflows, dependencies, and decision points.
                                </p>
                                <Button
                                    onClick={handleGenerateBusinessProcess}
                                    disabled={isGeneratingBp}
                                    className="relative z-10 bg-gradient-to-r from-primary to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white rounded-xl px-8 h-12 font-black flex items-center gap-3 shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all hover:scale-105 border border-orange-400/30 uppercase tracking-widest text-[11px]"
                                >
                                    <SparklesIcon className={`h-5 w-5 ${isGeneratingBp ? 'animate-spin' : ''}`} />
                                    {isGeneratingBp ? 'Compiling Flow...' : 'Generate Process'}
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Flowchart Panel */}
                                <div className="lg:col-span-2 glass-panel rounded-2xl border border-white/10 bg-black/30 p-6 flex flex-col justify-between min-h-[500px] relative overflow-hidden">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-[10px] font-black uppercase tracking-widest bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-md">
                                            Flowchart View
                                        </span>
                                        <div className="text-[10px] text-text-secondary">
                                            Generated At: {new Date(project.businessProcess.generatedAt).toLocaleString()}
                                        </div>
                                    </div>

                                    <div className="flex-1 flex justify-center items-center bg-black/50 border border-white/5 rounded-xl overflow-hidden min-h-[400px]">
                                        <MermaidDiagram chart={project.businessProcess.mermaidCode} />
                                    </div>
                                </div>

                                {/* Mermaid Code Editor Panel */}
                                <div className="glass-panel rounded-2xl border border-white/10 bg-black/30 p-6 flex flex-col min-h-[500px]">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-[10px] font-black uppercase tracking-widest bg-info/10 text-info border border-info/20 px-2.5 py-1 rounded-md">
                                            Mermaid Syntax
                                        </span>
                                        <button
                                            onClick={() => setIsEditingBp(!isEditingBp)}
                                            className="text-[10px] font-bold text-primary hover:underline"
                                        >
                                            {isEditingBp ? 'Cancel Edit' : 'Edit Code'}
                                        </button>
                                    </div>

                                    <textarea
                                        value={bpCode}
                                        onChange={(e) => setBpCode(e.target.value)}
                                        readOnly={!isEditingBp}
                                        className={`flex-1 w-full bg-black/60 font-mono text-[11px] p-4 rounded-xl border focus:outline-none transition-all resize-none ${isEditingBp
                                                ? 'border-primary/50 text-white focus:ring-1 focus:ring-primary/50'
                                                : 'border-white/5 text-text-secondary/70 cursor-not-allowed'
                                            }`}
                                        placeholder="Mermaid TD flowchart syntax here..."
                                    />

                                    {isEditingBp && (
                                        <Button
                                            onClick={handleSaveBusinessProcess}
                                            className="mt-4 bg-gradient-to-r from-primary to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white rounded-xl py-3.5 font-black uppercase tracking-widest text-[11px] w-full"
                                        >
                                            Save Diagram Changes
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!selectedReqId && !['project', 'estimation', 'test-cases', 'token-usage', 'product-intelligence', 'strategic-initiatives', 'integrations', 'bp'].includes(activeTab) && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 relative z-10">
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex items-center gap-2">
                                <h1 className="text-3xl font-black text-white whitespace-nowrap tracking-tight">
                                    {activeTab === 'brd' ? 'Business Requirements' :
                                        activeTab === 'prd' ? 'Product Requirements' :
                                            activeTab === 'nfr' ? 'Non Functional Requirements' :
                                                activeTab === 'uir' ? 'User Interface Requirements' :
                                                    'Business Processes'}
                                </h1>
                                <div className="p-1.5 bg-white/5 rounded-full border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                                    <InformationCircleIcon className="h-5 w-5 text-text-secondary" />
                                </div>
                            </div>
                                <div className="flex items-center gap-2">
                                <div className="relative group">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className={`flex items-center gap-2 font-bold transition-all duration-200 uppercase tracking-widest text-[10px] ${isTabVerified ? 'bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`}
                                    onClick={handleToggleVerifyType}
                                >
                                    <CheckCircleIcon className={`h-4 w-4 ${isTabVerified ? 'text-green-400' : ''}`} />
                                    Verified
                                    <InformationCircleIcon className="h-3.5 w-3.5 opacity-60 hover:opacity-100 transition-opacity ml-0.5" />
                                </Button>
                                    
                                    {/* Custom Tooltip */}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 p-3 bg-black/90 border border-white/10 rounded-xl text-[11px] text-white/90 normal-case font-medium tracking-normal leading-relaxed shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-1 border-4 border-transparent border-b-black/90" />
                                        Mark this document as verified. Verification indicates it is ready for implementation. Editing is locked only after all core documents (BRD, PRD, UIX, and NFR) have been verified.
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-primary/10 border-primary/30 text-primary hover:bg-primary/20 hover:text-white"
                                    onClick={handleAddManualRequirement}
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Add New
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-info/10 border-info/30 text-info hover:bg-info/20 hover:text-white"
                                    onClick={() => handleRegenerateBatch(activeTab.toUpperCase())}
                                    disabled={isGeneratingBatch || (activeTab !== 'bp' && prerequisitesMet)}
                                    title={(activeTab !== 'bp' && prerequisitesMet) ? "Regeneration is disabled because all core documents are verified." : undefined}
                                >
                                    <ArrowPathIcon className={`h-4 w-4 ${isGeneratingBatch ? 'animate-spin' : ''}`} />
                                    Regenerate
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2 font-black transition-all duration-200 uppercase tracking-widest text-[10px] bg-white/5 border-white/10 text-white hover:bg-white/10"
                                    onClick={() => triggerDownload(handleDownloadCurrentTab)}
                                    disabled={isExporting}
                                >
                                    <ArrowDownTrayIcon className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
                                    Export {activeTab.toUpperCase()} PDF
                                </Button>
                                </div>
                        </div>

                        {/* Verification info bar — shown below button row */}
                        <div className="flex items-start gap-2.5 mb-4 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <InformationCircleIcon className="h-4 w-4 text-white/40 flex-shrink-0 mt-0.5" />
                            <p className="text-[11px] text-white/40 leading-relaxed">
                                Mark this document as verified. Verification indicates it is ready for implementation. Editing is locked only after all core documents (BRD, PRD, UIX, and NFR) have been verified.
                            </p>
                        </div>

                        {/* Search Bar */}
                        <div className="relative mb-6">
                            <input
                                type="text"
                                placeholder="Search requirements..."
                                className="w-full bg-black/40 border border-white/10 rounded-xl py-3.5 px-5 text-white placeholder-text-secondary/50 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all shadow-inner font-medium text-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {reqCounts[activeTab.toUpperCase() as keyof typeof reqCounts] === 0 ? (
                            <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-black/20 rounded-2xl border-2 border-dashed border-white/10 relative overflow-hidden group">
                                <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-10 transition-opacity" />
                                <div className="mb-8 relative rounded-full p-4 bg-white/5 border border-white/10 shadow-[0_0_30px_rgba(255,107,0,0.1)]">
                                    <div className="absolute inset-0 bg-primary rounded-full blur-xl opacity-20 animate-pulse" />
                                    <img src="/DocSprintLogo.png" alt="DocSprint" className="h-32 w-32 object-contain relative z-10 drop-shadow-2xl brightness-110 filter hue-rotate-15" />
                                </div>
                                <h2 className="text-2xl font-black text-white mb-3 tracking-tight">
                                    {navigationItems.find(i => i.id === activeTab)?.name} not found
                                </h2>
                                <p className="text-text-secondary max-w-md mb-8 font-medium">
                                    You haven't generated any {navigationItems.find(i => i.id === activeTab)?.name} for this project yet. Deploy Agents to engineer them now or inject manual constraints.
                                </p>
                                <div className="flex gap-4 relative z-10 text-[11px]">
                                    <Button
                                        onClick={() => openGenModal(activeTab.toUpperCase() as any, false)}
                                        className="bg-gradient-to-r from-primary to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white rounded-xl px-8 h-12 font-black flex items-center gap-3 shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all hover:scale-105 border border-orange-400/30 uppercase tracking-widest"
                                    >
                                        <SparklesIcon className="h-5 w-5" />
                                        Generate via Engine
                                    </Button>
                                    <Button
                                        onClick={handleAddManualRequirement}
                                        variant="outline"
                                        className="border-white/10 text-white bg-white/5 hover:bg-white/10 rounded-xl px-8 h-12 font-black transition-all hover:scale-105 uppercase tracking-widest text-[11px]"
                                    >
                                        Manual Config
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {project.requirements
                                    ?.filter(r => r.type === activeTab.toUpperCase())
                                    .filter(r => r.title.toLowerCase().includes(searchTerm.toLowerCase()) || r.description?.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map((req, index) => {
                                        const colorScheme = {
                                            BRD: {
                                                border: 'border-blue-500/10',
                                                hoverBorder: 'hover:border-blue-500/30',
                                                hoverShadow: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]',
                                                leftAccent: 'bg-gradient-to-b from-blue-400 to-blue-600',
                                                badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20 group-hover:bg-blue-500/20',
                                                title: 'group-hover:text-blue-400',
                                                icon: 'text-blue-400'
                                            },
                                            PRD: {
                                                border: 'border-purple-500/10',
                                                hoverBorder: 'hover:border-purple-500/30',
                                                hoverShadow: 'hover:shadow-[0_0_20px_rgba(168,85,247,0.15)]',
                                                leftAccent: 'bg-gradient-to-b from-purple-400 to-purple-600',
                                                badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20 group-hover:bg-purple-500/20',
                                                title: 'group-hover:text-purple-400',
                                                icon: 'text-purple-400'
                                            },
                                            NFR: {
                                                border: 'border-emerald-500/10',
                                                hoverBorder: 'hover:border-emerald-500/30',
                                                hoverShadow: 'hover:shadow-[0_0_20px_rgba(10,185,129,0.15)]',
                                                leftAccent: 'bg-gradient-to-b from-emerald-400 to-emerald-600',
                                                badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 group-hover:bg-emerald-500/20',
                                                title: 'group-hover:text-emerald-400',
                                                icon: 'text-emerald-400'
                                            },
                                            UIR: {
                                                border: 'border-primary/10',
                                                hoverBorder: 'hover:border-primary/30',
                                                hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,107,0,0.15)]',
                                                leftAccent: 'bg-gradient-to-b from-primary to-orange-600',
                                                badge: 'bg-primary/10 text-primary border-primary/20 group-hover:bg-primary/20',
                                                title: 'group-hover:text-primary',
                                                icon: 'text-primary'
                                            },
                                            BP: {
                                                border: 'border-info/10',
                                                hoverBorder: 'hover:border-info/30',
                                                hoverShadow: 'hover:shadow-[0_0_20px_rgba(14,165,233,0.15)]',
                                                leftAccent: 'bg-gradient-to-b from-info to-blue-500',
                                                badge: 'bg-info/10 text-info border-info/20 group-hover:bg-info/20',
                                                title: 'group-hover:text-info',
                                                icon: 'text-info'
                                            }
                                        }[activeTab.toUpperCase() as 'BRD' | 'PRD' | 'NFR' | 'UIR' | 'BP'] || {
                                            border: 'border-white/5',
                                            hoverBorder: 'hover:border-white/20',
                                            hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,255,255,0.05)]',
                                            leftAccent: 'bg-white/40',
                                            badge: 'bg-white/5 text-text-secondary border-white/10 group-hover:bg-white/10 group-hover:text-white',
                                            title: 'group-hover:text-white',
                                            icon: 'text-text-secondary'
                                        };

                                        return (
                                            <div
                                                key={req.id}
                                                onClick={() => setSelectedReqId(req.id)}
                                                className={`glass-panel border border-white/5 ${colorScheme.hoverBorder} rounded-2xl ${colorScheme.hoverShadow} transition-all duration-200 cursor-pointer group relative overflow-hidden`}
                                            >
                                                {/* Left colored accent bar */}
                                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${colorScheme.leftAccent} opacity-30 group-hover:opacity-100 transition-opacity`}></div>

                                                {/* Card Content with consistent padding */}
                                                <div className="pl-6 pr-6 py-6">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className={`text-[10px] font-black uppercase tracking-widest ${colorScheme.badge} px-2.5 py-1 rounded-md border`}>
                                                            {activeTab.toUpperCase()}{String(index + 1).padStart(2, '0')}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {req.isVerified && (
                                                                <div className="bg-green-500/10 border border-green-500/30 rounded-full p-1 animate-in zoom-in duration-300">
                                                                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                                                                </div>
                                                            )}
                                                            {activeTab === 'prd' && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="text-[10px] px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 font-bold hover:bg-purple-500 hover:text-white h-7 transition-colors uppercase tracking-widest shadow-inner shadow-purple-500/10"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleUserStoryNavigation(req.id);
                                                                    }}
                                                                >
                                                                    Stories
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <h3 className={`text-lg font-black text-white mb-2 ${colorScheme.title} transition-colors tracking-tight`}>{req.title}</h3>
                                                    <p className="text-sm text-text-secondary font-medium line-clamp-2 leading-relaxed mb-4">
                                                        {stripHtml(req.description)}
                                                    </p>

                                                    {/* Linkage Badges */}
                                                    {activeTab === 'prd' && (req.linkedIds?.length || 0) > 0 && (() => {
                                                        const count = req.linkedIds?.length || 0;
                                                        return (
                                                            <div className="flex items-center gap-1.5 text-[10px] font-black text-orange-400 uppercase tracking-widest bg-orange-500/10 border border-orange-500/20 w-fit px-2 py-1 rounded">
                                                                <LinkIcon className="h-3 w-3" />
                                                                Linked: {count} BRD{count > 1 ? 's' : ''}
                                                            </div>
                                                        );
                                                    })()}

                                                    {activeTab === 'brd' && (() => {
                                                        const linkedCount = project.requirements?.filter(r => (r.linkedIds || []).includes(req.id)).length || 0;
                                                        if (linkedCount === 0) return null;
                                                        return (
                                                            <div className="flex items-center gap-1.5 text-[10px] font-black text-purple-400 uppercase tracking-widest bg-purple-500/10 border border-purple-500/20 w-fit px-2 py-1 rounded">
                                                                <QueueListIcon className="h-3 w-3" />
                                                                {linkedCount} Linked PRD{linkedCount > 1 ? 's' : ''}
                                                            </div>
                                                        );
                                                    })()}

                                                    {activeTab === 'prd' && (
                                                        <div className="space-y-3 mt-5 pt-5 border-t border-white/5">
                                                            <div>
                                                                <h4 className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1.5 flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-info" /> Screens</h4>
                                                                <p className="text-[11px] text-info/80 leading-relaxed font-mono bg-info/5 p-2 rounded-lg border border-info/10">{req.screens || 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <h4 className="text-[9px] font-black text-text-secondary uppercase tracking-widest mb-1.5 flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-green-500" /> Personas</h4>
                                                                <p className="text-[11px] text-green-400/80 leading-relaxed font-mono bg-green-500/5 p-2 rounded-lg border border-green-500/10">{req.personas || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                )}

                {(selectedReqId && (selectedRequirement || selectedReqId === 'new')) && (() => {
                    const colorSchemes = {
                        BRD: {
                            border: 'border-blue-500/20',
                            focusBorder: 'focus-within:border-blue-500/50',
                            focusRing: 'focus-within:ring-blue-500/30',
                            badge: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
                            accent: 'bg-gradient-to-r from-blue-400 to-blue-600',
                            button: 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-500',
                            enhanceButton: 'text-blue-400 border-blue-500/30 hover:bg-blue-500/20'
                        },
                        PRD: {
                            border: 'border-purple-500/20',
                            focusBorder: 'focus-within:border-purple-500/50',
                            focusRing: 'focus-within:ring-purple-500/30',
                            badge: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
                            accent: 'bg-gradient-to-r from-purple-400 to-purple-600',
                            button: 'bg-purple-600 hover:bg-purple-500 focus:ring-purple-500',
                            enhanceButton: 'text-purple-400 border-purple-500/30 hover:bg-purple-500/20'
                        },
                        NFR: {
                            border: 'border-emerald-500/20',
                            focusBorder: 'focus-within:border-emerald-500/50',
                            focusRing: 'focus-within:ring-emerald-500/30',
                            badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
                            accent: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
                            button: 'bg-emerald-600 hover:bg-emerald-500 focus:ring-emerald-500',
                            enhanceButton: 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                        },
                        UIR: {
                            border: 'border-primary/20',
                            focusBorder: 'focus-within:border-primary/50',
                            focusRing: 'focus-within:ring-primary/30',
                            badge: 'bg-primary/10 text-primary border-primary/30',
                            accent: 'bg-gradient-to-r from-primary to-orange-600',
                            button: 'bg-primary hover:bg-orange-500 focus:ring-primary',
                            enhanceButton: 'text-primary border-primary/30 hover:bg-primary/20'
                        },
                        BP: {
                            border: 'border-info/20',
                            focusBorder: 'focus-within:border-info/50',
                            focusRing: 'focus-within:ring-info/30',
                            badge: 'bg-info/10 text-info border-info/30',
                            accent: 'bg-gradient-to-r from-info to-blue-500',
                            button: 'bg-info hover:bg-blue-500 focus:ring-info',
                            enhanceButton: 'text-info border-info/30 hover:bg-info/20'
                        },
                    };

                    const editColorScheme = (colorSchemes as any)[activeTab.toUpperCase()] || {
                        border: 'border-white/10',
                        focusBorder: 'focus-within:border-white/30',
                        focusRing: 'focus-within:ring-white/10',
                        badge: 'bg-white/5 text-text-secondary',
                        accent: 'bg-white/40',
                        button: 'bg-white/10 hover:bg-white/20 text-white',
                        enhanceButton: 'text-white border-white/10 hover:bg-white/5'
                    };

                    return (
                        <>
                            <div className="glass-panel rounded-2xl border border-white/10 overflow-hidden animate-in fade-in slide-in-from-left-4 duration-300 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                                {/* Colored top accent bar */}
                                <div className={`h-1.5 ${editColorScheme.accent} opacity-50`}></div>

                                <div className="p-8 space-y-8 relative z-10">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setSelectedReqId(null)}
                                                className="p-1.5 hover:bg-white/10 rounded-lg text-text-secondary hover:text-white transition-colors flex items-center gap-1.5 group"
                                                title="Back to List"
                                            >
                                                <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-0.5 transition-transform" />
                                            </button>
                                            <div className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest border ${editColorScheme.badge}`}>
                                                {(() => {
                                                    const reqs = project.requirements?.filter(r => r.type === activeTab.toUpperCase()) || [];
                                                    const index = reqs.findIndex(r => r.id === selectedReqId);
                                                    return `${activeTab.toUpperCase()}${String(index !== -1 ? index + 1 : reqs.length + 1).padStart(2, '0')}`;
                                                })()}
                                            </div>
                                            <h2 className="text-2xl font-black text-white tracking-tight">Edit Requirement</h2>
                                        </div>
                                        <div className="flex gap-2">
                                            {activeTab === 'bp' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-info bg-info/10 border-info/30 hover:bg-info/20 hover:text-white font-black uppercase tracking-widest flex items-center gap-2 text-[10px]"
                                                    onClick={() => {
                                                        if (selectedReqId && savedFlows[selectedReqId]) {
                                                            setFlowSteps(savedFlows[selectedReqId].steps);
                                                            setFlowTheme(savedFlows[selectedReqId].theme);
                                                        } else {
                                                            handleRegenerateFlow();
                                                        }
                                                        setIsFlowView(true);
                                                    }}
                                                >
                                                    <PresentationChartBarIcon className="h-4 w-4" />
                                                    View Flow
                                                </Button>
                                            )}
                                            {activeTab === 'prd' && (
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-primary bg-primary/10 border-primary/30 hover:bg-primary/20 hover:text-white font-black uppercase tracking-widest flex items-center gap-2 text-[10px]"
                                                        onClick={() => triggerDownload(() => handleDownloadPRDImplementation(selectedReqId!))}
                                                        disabled={isExporting}
                                                    >
                                                        <ArrowDownTrayIcon className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
                                                        Implementation Pack
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-purple-400 bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 hover:text-white font-black uppercase tracking-widest flex items-center gap-2 text-[10px]"
                                                        onClick={() => handleUserStoryNavigation(selectedReqId!)}
                                                    >
                                                        <DocumentTextIcon className="h-4 w-4" />
                                                        User Stories
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500 bg-red-500/10 border-red-500/30 hover:bg-red-500/20 hover:text-white p-2"
                                                        onClick={() => setShowDeleteModal(true)}
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-primary" /> Title <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                                className={`w-full bg-black/40 border ${editColorScheme.border} ${editColorScheme.focusBorder} rounded-xl p-3.5 text-sm text-white placeholder-text-secondary/50 focus:ring-1 ${editColorScheme.focusRing} outline-none transition-all font-bold shadow-inner`}
                                                placeholder="Enter requirement title..."
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-info" /> Description <span className="text-red-500">*</span>
                                            </label>
                                            <div className="border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all shadow-inner bg-black/40">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editDescription}
                                                    onChange={setEditDescription}
                                                    placeholder="Enter detailed requirement description..."
                                                    className="requirement-editor"
                                                    modules={{
                                                        toolbar: [
                                                            [{ 'header': [1, 2, 3, false] }],
                                                            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                            ['link', 'code-block'],
                                                            ['clean']
                                                        ]
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4 pt-6 border-t border-white/10">
                                            <div className="flex justify-between items-center">
                                                <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest">
                                                    {activeTab === 'prd' ? 'Linked BRDs' : activeTab === 'bp' ? 'Included BRDs & PRDs' : 'Linked Requirements'}
                                                </label>
                                                <div className="flex items-center gap-3">
                                                    {activeTab === 'prd' && selectedUserStories.length > 0 && (
                                                        <span className="text-[10px] font-black tracking-widest text-purple-400 bg-purple-500/10 px-2.5 py-1 rounded-md border border-purple-500/30 uppercase">
                                                            {selectedUserStories.length} User Stories | {selectedUserStories.reduce((acc, s) => acc + (s.tasks?.length || 0), 0)} Tasks
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={() => setIsLinking(!isLinking)}
                                                        className="text-[10px] font-black tracking-widest text-primary hover:text-orange-400 uppercase flex items-center gap-1.5 transition-colors bg-primary/5 px-2.5 py-1.5 rounded-lg border border-primary/20"
                                                    >
                                                        {isLinking ? <XMarkIcon className="h-3 w-3" /> : <PlusIcon className="h-3 w-3" />}
                                                        {isLinking ? 'Close' : 'Link Requirement'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="space-y-4">
                                                {isLinking && (
                                                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200 bg-black/20 p-4 rounded-2xl border border-white/5 shadow-inner">
                                                        <div className="relative">
                                                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-secondary/50" />
                                                            <input
                                                                type="text"
                                                                placeholder="Search requirements to link..."
                                                                value={linkSearchTerm}
                                                                onChange={(e) => setLinkSearchTerm(e.target.value)}
                                                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-[11px] text-white placeholder-text-secondary/30 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                                                            />
                                                        </div>
                                                        <div className="max-h-[220px] overflow-y-auto custom-scrollbar space-y-1 pr-1">
                                                            {(() => {
                                                                const available = project.requirements?.filter(r => {
                                                                    if (r.id === selectedReqId) return false;
                                                                    if (selectedLinkedIds.includes(r.id)) return false;

                                                                    // Filtering based on tab logic
                                                                    if (activeTab === 'prd') {
                                                                        if (r.type !== 'BRD') return false;
                                                                    } else if (activeTab === 'bp') {
                                                                        if (r.type !== 'BRD' && r.type !== 'PRD') return false;
                                                                    } else if (activeTab === 'nfr' || activeTab === 'uir') {
                                                                        if (r.type !== 'BRD' && r.type !== 'PRD') return false;
                                                                    }

                                                                    if (linkSearchTerm) {
                                                                        return r.title.toLowerCase().includes(linkSearchTerm.toLowerCase());
                                                                    }
                                                                    return true;
                                                                }) || [];

                                                                if (available.length === 0) {
                                                                    return <p className="text-[10px] text-text-secondary/50 text-center py-6 italic font-medium">No linkable requirements found</p>;
                                                                }

                                                                return available.map(req => {
                                                                    const reqType = req.type;
                                                                    const typeReqs = project.requirements?.filter(r => r.type === reqType) || [];
                                                                    const reqIndex = typeReqs.findIndex(r => r.id === req.id);
                                                                    const config = {
                                                                        BRD: { color: 'blue-400', bg: 'blue-500/10', border: 'blue-500/20', icon: BriefcaseIcon },
                                                                        PRD: { color: 'purple-400', bg: 'purple-500/10', border: 'purple-500/20', icon: QueueListIcon },
                                                                    }[reqType as 'BRD' | 'PRD'] || { color: 'text-secondary', bg: 'white/5', border: 'white/10', icon: LinkIcon };

                                                                    return (
                                                                        <div
                                                                            key={req.id}
                                                                            onClick={() => { handleToggleLink(req.id); setLinkSearchTerm(''); }}
                                                                            className="flex items-center justify-between p-2.5 bg-white/5 border border-white/5 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer group"
                                                                        >
                                                                            <div className="flex items-center gap-3">
                                                                                <div className={`p-1.5 rounded-lg bg-${config.bg} border border-${config.border}`}>
                                                                                    <config.icon className={`h-3.5 w-3.5 text-${config.color}`} />
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-[11px] font-bold text-white line-clamp-1">{req.title}</p>
                                                                                    <p className={`text-[9px] font-black tracking-widest text-${config.color} uppercase`}>{reqType}{String(reqIndex + 1).padStart(2, '0')}</p>
                                                                                </div>
                                                                            </div>
                                                                            <PlusIcon className="h-4 w-4 text-text-secondary/50 group-hover:text-primary transition-colors" />
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center">
                                                    {selectedLinkedIds.length > 0 && (
                                                        <span className="text-[10px] font-black tracking-widest text-text-secondary bg-white/5 px-2 py-0.5 rounded-md border border-white/10 uppercase">
                                                            {selectedLinkedIds.length} Linked
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {selectedLinkedIds.length === 0 ? (
                                                        <p className="text-[11px] text-text-secondary/50 font-medium italic bg-white/5 p-3 rounded-xl border border-dashed border-white/10 w-full text-center">
                                                            {activeTab === 'bp' ? 'No BRDs or PRDs linked. Maintain context hierarchy.' : 'No BRDs linked. Select from context hierarchy.'}
                                                        </p>
                                                    ) : (
                                                        selectedLinkedIds.map(id => {
                                                            const req = project.requirements?.find(r => r.id === id);
                                                            if (!req) return null;

                                                            const reqType = req.type;
                                                            const typeReqs = project.requirements?.filter(r => r.type === reqType) || [];
                                                            const reqIndex = typeReqs.findIndex(r => r.id === id);

                                                            const config = {
                                                                BRD: { color: 'blue-400', bg: 'blue-500/10', border: 'blue-500/20', icon: BriefcaseIcon },
                                                                PRD: { color: 'purple-400', bg: 'purple-500/10', border: 'purple-500/20', icon: QueueListIcon },
                                                            }[reqType as 'BRD' | 'PRD'] || { color: 'text-secondary', bg: 'white/5', border: 'white/10', icon: LinkIcon };

                                                            return (
                                                                <div key={id} className={`flex items-center gap-2 pl-3 pr-2 py-1.5 bg-black/40 border border-${config.border} rounded-xl group hover:border-${config.color.split('-')[0]}-500/50 transition-all`}>
                                                                    <config.icon className={`h-4 w-4 text-${config.color}`} />
                                                                    <span className={`text-[10px] font-black tracking-widest text-${config.color}`}>{reqType}{String(reqIndex + 1).padStart(2, '0')}</span>
                                                                    <span className="text-[11px] font-medium text-white">{req.title}</span>
                                                                    <button
                                                                        onClick={() => handleToggleLink(id)}
                                                                        className={`p-1 hover:bg-white/10 rounded-md text-text-secondary hover:text-white transition-colors`}
                                                                    >
                                                                        <XMarkIcon className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>

                                                {activeTab === 'brd' && (() => {
                                                    const linkedPrds = project.requirements?.filter(r => (r.linkedIds || []).includes(selectedReqId)) || [];
                                                    if (linkedPrds.length === 0) return null;

                                                    return (
                                                        <div className="mt-5 pt-5 border-t border-white/5">
                                                            <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest block mb-3">Linked in PRDs</label>
                                                            <div className="flex flex-wrap gap-2">
                                                                {linkedPrds.map((prd) => (
                                                                    <span key={prd.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-xl text-[10px] font-black tracking-widest text-purple-400 hover:bg-purple-500/20 hover:text-white transition-colors cursor-pointer uppercase" onClick={() => { setSelectedReqId(prd.id); setSearchParams({ tab: 'prd' }); }}>
                                                                        <QueueListIcon className="h-3.5 w-3.5" />
                                                                        PRD{String(project.requirements?.filter(r => r.type === 'PRD').findIndex(r => r.id === prd.id)! + 1).padStart(2, '0')}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {activeTab === 'prd' && selectedUserStories.length > 0 && (
                                                <div className="mt-8 space-y-4">
                                                    <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest block">Coverage Summary</label>
                                                    <div className="grid grid-cols-1 gap-2">
                                                        {selectedUserStories.slice(0, 5).map((story, idx) => (
                                                            <div key={story.id} className="flex items-center justify-between p-3.5 bg-black/20 rounded-xl border border-white/5 group hover:border-white/20 transition-all cursor-pointer">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="h-7 w-7 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[10px] font-black flex items-center justify-center">
                                                                        S{idx + 1}
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs font-bold text-white line-clamp-1">{story.title}</p>
                                                                        <p className="text-[10px] font-medium text-text-secondary mt-0.5">{story.tasks?.length || 0} implementation tasks</p>
                                                                    </div>
                                                                </div>
                                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <ArrowDownTrayIcon className="h-4 w-4 text-text-secondary" />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {selectedUserStories.length > 5 && (
                                                            <p className="text-[10px] font-bold text-center text-text-secondary/50 pt-2 uppercase tracking-widest">
                                                                + {selectedUserStories.length - 5} more stories. View "User Stories" for full breakdown.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {activeTab === 'prd' && selectedUserStories.length === 0 && !isLoadingStories && (
                                                <div className="mt-6 p-6 bg-purple-500/5 rounded-2xl border border-dashed border-purple-500/20 text-center">
                                                    <p className="text-[11px] font-black tracking-widest text-purple-400/80 uppercase mb-3">No implementation data yet</p>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-[10px] font-black tracking-widest text-purple-400 hover:text-white uppercase h-auto p-0"
                                                        onClick={() => handleUserStoryNavigation(selectedReqId!)}
                                                    >
                                                        Init User Stories Generation →
                                                    </Button>
                                                </div>
                                            )}
                                            {isLoadingStories && (
                                                <div className="animate-pulse space-y-3 mt-6">
                                                    <div className="h-12 bg-white/5 border border-white/10 rounded-xl w-full" />
                                                    <div className="h-12 bg-white/5 border border-white/10 rounded-xl w-full" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center pt-8 border-t border-white/10">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleEnhanceRequirement}
                                            disabled={isEnhancing || !editDescription || (activeTab !== 'bp' && prerequisitesMet)}
                                            className={`font-black tracking-widest uppercase text-[10px] flex items-center gap-2 px-5 py-2.5 transition-colors ${editColorScheme.enhanceButton}`}
                                            title={(activeTab !== 'bp' && prerequisitesMet) ? "AI Enhancement is disabled because all core documents are verified." : undefined}
                                        >
                                            <SparklesIcon className={`h-4 w-4 ${isEnhancing ? 'animate-spin' : ''}`} />
                                            {isEnhancing ? 'Enhancing...' : 'Enhance with Engine'}
                                        </Button>
                                        <div className="flex gap-3">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="font-black tracking-widest uppercase text-[10px] border border-white/10 text-white bg-white/5 hover:bg-white/10 px-6 py-2.5 transition-colors"
                                                onClick={() => setSelectedReqId(null)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                className={`font-black tracking-widest uppercase text-[10px] text-white shadow-md px-6 py-2.5 transition-colors ${editColorScheme.button}`}
                                                onClick={handleUpdateRequirement}
                                            >
                                                {selectedReqId === 'new' ? 'Deploy Target' : 'Commit Changes'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Sidebar Completely Disabled per request */}
                            {false && (
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-[750px] animate-in fade-in slide-in-from-right-4 duration-300">
                                    <div className="p-4 border-b border-gray-100 flex gap-2">
                                        {/* Talk to hidden
                                        <button
                                            onClick={() => setRightSidebarTab('chat')}
                                            className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${rightSidebarTab === 'chat'
                                                ? 'bg-orange-50 text-orange-700 border border-orange-100 shadow-sm'
                                                : 'text-gray-400 hover:bg-gray-50'
                                                }`}
                                        >
                                            <SparklesIcon className="h-4 w-4" />
                                            Talk to
                                            <span className="bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded text-[10px] font-bold border border-orange-200 uppercase">Beta</span>
                                        </button>
                                        */}
                                        {(activeTab === 'prd' || activeTab === 'bp') && (
                                            <button
                                                onClick={() => setRightSidebarTab('link')}
                                                className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${rightSidebarTab === 'link'
                                                    ? (activeTab === 'bp' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-purple-100 text-purple-700 border border-purple-200 shadow-sm')
                                                    : 'text-gray-400 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {activeTab === 'bp' ? 'Include BRDs & PRDs' : 'Include BRDs'}
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                        {/* Talk to Chat UI Hidden
                                        {rightSidebarTab === 'chat' ? (
                                            <div className="flex-1 overflow-y-auto space-y-4">
                                                <div className="flex gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                                        <SparklesIcon className="h-4 w-4 text-orange-600" />
                                                    </div>
                                                    <div className="bg-gray-50 rounded-2xl p-4 text-sm text-gray-700 max-w-[85%] border border-gray-100">
                                                        <p>I'm ready to help! {activeTab === 'bp' ? "I can help you design this business process workflow, identify bottlenecks, or link related BRDs/PRDs." : "I can refine your personas, suggest technical edge cases, or help you link this PRD to the right business goals."}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                        */}
                                        {rightSidebarTab === 'link' && (
                                            <div className="space-y-4">
                                                {activeTab === 'bp' && (
                                                    <div className="flex bg-gray-50 p-1 rounded-xl mb-4">
                                                        <button
                                                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${rightSidebarTabSub === 'PRD' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400'}`}
                                                            onClick={() => setRightSidebarTabSub('PRD')}
                                                        >
                                                            PRD ({project?.requirements?.filter(r => r.type === 'PRD').length || 0})
                                                        </button>
                                                        <button
                                                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${rightSidebarTabSub === 'BRD' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400'}`}
                                                            onClick={() => setRightSidebarTabSub('BRD')}
                                                        >
                                                            BRD ({project?.requirements?.filter(r => r.type === 'BRD').length || 0})
                                                        </button>
                                                    </div>
                                                )}

                                                {project?.requirements
                                                    ?.filter(r => r.type === (activeTab === 'bp' ? rightSidebarTabSub : 'BRD'))
                                                    .map(req => {
                                                        const isSelected = selectedLinkedIds.includes(req.id);
                                                        const colorClass = activeTab === 'bp' ? 'indigo' : 'purple';

                                                        return (
                                                            <div
                                                                key={req.id}
                                                                className={`p-4 rounded-xl border-2 transition-all cursor-pointer group flex items-start gap-3 ${isSelected
                                                                    ? `bg-${colorClass}-50 border-${colorClass}-200`
                                                                    : 'bg-white border-gray-50 hover:border-gray-200'
                                                                    }`}
                                                                onClick={() => handleToggleLink(req.id)}
                                                            >
                                                                <div className="flex-1">
                                                                    <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isSelected ? `text-${colorClass}-600` : 'text-gray-400'}`}>
                                                                        {(() => {
                                                                            const reqsByType = project?.requirements?.filter(r => r.type === req.type) || [];
                                                                            const index = reqsByType.findIndex(r => r.id === req.id);
                                                                            return index !== -1 ? `${req.type}${String(index + 1).padStart(2, '0')}` : req.type;
                                                                        })()}
                                                                    </div>
                                                                    <h4 className={`text-sm font-bold leading-tight ${isSelected ? `text-${colorClass}-900` : 'text-gray-900'}`}>
                                                                        {req.title}
                                                                    </h4>
                                                                    <p className="text-[11px] text-gray-500 line-clamp-2 mt-1">
                                                                        {req.description}
                                                                    </p>
                                                                </div>
                                                                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center transition-all ${isSelected ? `bg-${colorClass}-600 border-${colorClass}-600` : 'border-gray-200 bg-white group-hover:border-gray-300'}`}>
                                                                    {isSelected && <ShieldCheckIcon className="h-3 w-3 text-white" />}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )
                })()}

                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteRequirement}
                    isLoading={isDeleting}
                    title={`Delete ${activeTab.toUpperCase()} Requirement`}
                    message={`Are you sure you want to delete this ${activeTab.toUpperCase()} requirement? This action will permanently remove all associated data and cannot be undone.`}
                    confirmLabel="Delete Requirement"
                    variant="danger"
                />

                {/* Generation Modal */}
                {isGenModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-200" onClick={() => !isGeneratingBatch && setIsGenModalOpen(false)} />
                        <div className="relative glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                            <div className="p-8 relative z-10">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="mb-2 relative">
                                        <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                                        <img src="/DocSprintLogo.png" alt="DocSprint" className="h-24 w-24 object-contain relative z-10" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-white tracking-tight">{isRegenerateMode ? 'Regenerate' : 'Generate'} {genType}</h3>
                                        <p className="text-sm text-text-secondary">Configure generation.</p>
                                    </div>
                                </div>

                                {isRegenerateMode && (
                                    <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-xl flex gap-3 animate-in slide-in-from-top-2 duration-300">
                                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-black text-red-400 uppercase tracking-widest">High Impact Action</p>
                                            <p className="text-xs text-red-300/80 leading-relaxed mt-1 font-medium">
                                                This will <span className="font-bold text-red-200">permanently delete</span> all current {genType} records for this target and replace them with a fresh matrix.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest">
                                                Target Count
                                            </label>
                                            <span className={`${isRegenerateMode ? 'bg-red-500/10 text-red-400 border-red-500/30' : 'bg-primary/10 text-primary border-primary/30'} px-3 py-1 rounded-lg font-black text-sm border`}>
                                                {genCount}
                                            </span>
                                        </div>
                                        <div className="px-1">
                                            <Slider
                                                value={genCount}
                                                min={1}
                                                max={genType === 'BP' ? 15 : 30}
                                                step={1}
                                                onChange={setGenCount}
                                                disabled={isGeneratingBatch}
                                            />
                                        </div>
                                        <div className="flex justify-between text-[10px] font-black text-text-secondary/50 uppercase tracking-widest px-1">
                                            <span>1 Item</span>
                                            <span>{genType === 'BP' ? 15 : 30} Items</span>
                                        </div>
                                    </div>

                                    {isRegenerateMode && (
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                                Regeneration Thoughts / Feedback (Optional)
                                            </label>
                                            <div className="border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all shadow-inner bg-black/40">
                                                <textarea
                                                    rows={3}
                                                    value={userThoughts}
                                                    onChange={(e) => setUserThoughts(e.target.value)}
                                                    className="w-full p-4 text-sm text-white leading-relaxed outline-none resize-none font-medium bg-transparent placeholder-text-secondary/30 custom-scrollbar"
                                                    placeholder="Provide your thoughts, vision, or feedback on previous output..."
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="bg-black/40 p-5 rounded-xl border border-white/5 shadow-inner">
                                        <p className="text-[11px] text-text-secondary/80 leading-relaxed font-medium">
                                            {isRegenerateMode
                                                ? `The system will perform a deep re-analysis of your target to generate a higher-quality, cohesive matrix of ${genCount} ${genType} requirements.`
                                                : `The system will analyze the target context and technical vectors to generate a cohesive matrix of ${genCount} ${genType} requirements.`}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-black/40 px-8 py-5 flex justify-end gap-3 border-t border-white/5 relative z-10">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsGenModalOpen(false)}
                                    disabled={isGeneratingBatch}
                                    className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase px-5 transition-colors"
                                >
                                    Abort
                                </Button>
                                <Button
                                    onClick={handleGenerateBatch}
                                    loading={isGeneratingBatch}
                                    className={`rounded-xl px-6 font-black tracking-widest text-[10px] uppercase shadow-lg transition-all ${isRegenerateMode
                                        ? 'bg-red-600 hover:bg-red-500 text-white shadow-red-600/20'
                                        : 'bg-primary hover:bg-orange-500 text-white shadow-primary/20'
                                        }`}
                                >
                                    {isRegenerateMode ? 'Confirm & Purge Matrix' : 'Initialize Generation'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Flowchart View Modal */}
                {isFlowView && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-8">
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setIsFlowView(false)} />
                        <div className="relative glass-panel rounded-[2rem] shadow-[0_0_80px_rgba(0,0,0,0.6)] w-full max-w-6xl h-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 border border-white/10">
                            {/* Header */}
                            <div className="p-6 border-b border-white/10 bg-black/40 flex justify-between items-center sticky top-0 z-10 backdrop-blur-xl">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-2xl bg-info/10 text-info border border-info/30">
                                        <PresentationChartBarIcon className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-white tracking-tight">
                                            {(() => {
                                                const reqs = project?.requirements?.filter(r => r.type === 'BP') || [];
                                                const index = reqs.findIndex(r => r.id === selectedReqId);
                                                return `BP${String(index !== -1 ? index + 1 : reqs.length + 1).padStart(2, '0')}`;
                                            })()} <span className="text-white/30 mx-2">/</span> {editTitle}
                                        </h3>
                                        <p className="text-[11px] font-black uppercase tracking-widest text-text-secondary mt-1">Business Process Routing Matrix</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Button
                                        variant="outline"
                                        className={`font-black uppercase tracking-widest text-[10px] border-${flowTheme}-500/30 text-${flowTheme}-400 bg-${flowTheme}-500/10 hover:bg-${flowTheme}-500/20 hover:text-white transition-colors px-5`}
                                        onClick={handleRegenerateFlow}
                                        loading={isGeneratingBatch}
                                    >
                                        <SparklesIcon className="h-4 w-4 mr-2" />
                                        Recompute Matrix
                                    </Button>
                                    <button
                                        onClick={() => setIsFlowView(false)}
                                        className="p-2 hover:bg-white/10 rounded-xl transition-colors text-text-secondary hover:text-white"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            {/* Flow Area */}
                            <div className="flex-1 overflow-auto p-12 flex justify-center custom-scrollbar bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:20px_20px] bg-[#0f172a]">
                                <div ref={flowRef} id="workflow-capture-area" key={flowVersion} className="w-full min-w-[1000px] h-fit flex flex-col items-center pb-40 pt-20 relative bg-transparent">

                                    {/* Standard Precision Architectural Marker Definition */}
                                    <svg width="0" height="0" className="absolute">
                                        <defs>
                                            <marker id="architect-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                                                <path d="M 0 1.5 L 10 5 L 0 8.5 Z" fill="#94A3B8" />
                                            </marker>
                                        </defs>
                                    </svg>

                                    {/* Start Entry Point */}
                                    <div className="flex flex-col items-center">
                                        <div className="px-14 py-4 bg-[#1E293B] border-2 border-[#334155] rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.5)] z-10 flex items-center gap-3">
                                            <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399] animate-pulse"></div>
                                            <span className="text-[12px] font-black text-white uppercase tracking-[0.4em]">INIT PROCESS</span>
                                        </div>
                                        <svg width="2" height="60" className="overflow-visible">
                                            <line x1="1" y1="0" x2="1" y2="60" stroke="#475569" strokeWidth="2" markerEnd="url(#architect-arrow)" strokeDasharray="4 4" />
                                        </svg>
                                    </div>

                                    <div className="flex flex-col items-center w-full px-10">
                                        {(() => {
                                            const themeColors: Record<string, { bg: string, border: string, text: string, accent: string, marker: string, ripple: string }> = {
                                                indigo: { bg: 'bg-[#1e1b4b]', border: 'border-indigo-500/30', text: 'text-indigo-200', accent: 'bg-indigo-500', marker: '#6366f1', ripple: 'bg-indigo-400' },
                                                purple: { bg: 'bg-[#2e1065]', border: 'border-purple-500/30', text: 'text-purple-200', accent: 'bg-purple-500', marker: '#a855f7', ripple: 'bg-purple-400' },
                                                emerald: { bg: 'bg-[#064e3b]', border: 'border-emerald-500/30', text: 'text-emerald-200', accent: 'bg-emerald-500', marker: '#10b981', ripple: 'bg-emerald-400' },
                                                orange: { bg: 'bg-[#431407]', border: 'border-orange-500/30', text: 'text-orange-200', accent: 'bg-orange-500', marker: '#f97316', ripple: 'bg-orange-400' },
                                                cyan: { bg: 'bg-[#083344]', border: 'border-cyan-500/30', text: 'text-cyan-200', accent: 'bg-cyan-500', marker: '#06b6d4', ripple: 'bg-cyan-400' },
                                                rose: { bg: 'bg-[#4c0519]', border: 'border-rose-500/30', text: 'text-rose-200', accent: 'bg-rose-500', marker: '#f43f5e', ripple: 'bg-rose-400' }
                                            };
                                            const colors = themeColors[flowTheme] || themeColors.indigo;

                                            const renderWorkflow = (steps: any[], isNested = false) => {
                                                return steps.map((step, idx) => (
                                                    <React.Fragment key={`${step.title}-${idx}`}>
                                                        {step.type === 'step' ? (
                                                            <div className="flex flex-col items-center w-full">
                                                                <div className={`${isNested ? 'w-[320px]' : 'w-[450px]'} bg-[#0f172a]/80 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl z-10 flex items-center gap-6 group hover:border-${flowTheme}-500/50 transition-all duration-300 relative overflow-hidden`}>
                                                                    <div className={`absolute top-0 left-0 w-1 flex flex-col h-full ${colors.accent}`}></div>
                                                                    <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-[14px] font-black text-white/70 group-hover:${colors.bg} group-hover:${colors.text} transition-colors border border-white/10`}>
                                                                        {step.num || idx + 1}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <div className="text-[14px] font-bold text-white uppercase tracking-tight leading-tight">
                                                                            {step.title}
                                                                        </div>
                                                                        <div className={`h-0.5 w-8 bg-white/10 mt-3 group-hover:${colors.ripple} transition-colors`}></div>
                                                                    </div>
                                                                </div>

                                                                {idx < steps.length - 1 && (
                                                                    <svg width="2" height="60" className="overflow-visible">
                                                                        <line x1="1" y1="0" x2="1" y2="60" stroke="#475569" strokeWidth="2" markerEnd="url(#architect-arrow)" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center w-full">
                                                                {/* Rigid Decision Diamond */}
                                                                <div className="relative flex items-center justify-center w-52 h-52 z-10">
                                                                    <div className={`absolute inset-0 rotate-45 ${colors.bg} border-2 ${colors.border} rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.3)] transform hover:rotate-90 transition-all duration-700`}></div>
                                                                    <div className={`absolute inset-4 rotate-45 border border-${flowTheme}-500/20`}></div>
                                                                    <div className="relative z-10 text-center p-6 max-w-[140px]">
                                                                        <div className="text-[9px] font-black text-white/50 uppercase tracking-[0.3em] mb-2 flex items-center justify-center gap-1">
                                                                            <div className={`w-1.5 h-1.5 rounded-full ${colors.accent} animate-pulse`}></div> VALIDATE
                                                                        </div>
                                                                        <div className="text-[13px] font-black text-white leading-tight uppercase tracking-tight">
                                                                            {step.title}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="w-full relative flex flex-col items-center mt-4">
                                                                    <div className="w-full h-32 relative">
                                                                        <svg viewBox="0 0 1000 120" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
                                                                            {/* YES BRANCH */}
                                                                            <path d="M 500 0 C 500 60, 250 60, 250 120" stroke={colors.marker} strokeWidth="2" fill="none" markerEnd="url(#architect-arrow)" />
                                                                            <text x="320" y="60" className={`text-[14px] font-black fill-${flowTheme}-400 tracking-[0.2em]`}>TRUE</text>

                                                                            {/* NO BRANCH */}
                                                                            <path d="M 500 0 C 500 60, 750 60, 750 120" stroke="#64748B" strokeWidth="2" fill="none" markerEnd="url(#architect-arrow)" strokeDasharray="6,6" />
                                                                            <text x="630" y="60" className="text-[14px] font-black fill-slate-500 tracking-[0.2em]">FALSE</text>
                                                                        </svg>
                                                                    </div>

                                                                    <div className="flex justify-between w-full px-4 relative mt-4">
                                                                        {/* LEFT LANE: YES FLOW */}
                                                                        <div className="flex flex-col items-center w-fit min-w-[350px]">
                                                                            {Array.isArray(step.yes) ? renderWorkflow(step.yes, true) : (
                                                                                <div className={`w-[300px] bg-[#0f172a]/90 backdrop-blur-md border border-${flowTheme}-500/50 p-5 rounded-2xl shadow-lg z-10 text-center relative overflow-hidden`}>
                                                                                    <div className={`absolute top-0 left-0 w-full h-1 ${colors.accent}`}></div>
                                                                                    <div className={`text-[10px] font-black text-${flowTheme}-400 uppercase tracking-widest mb-2 mt-1`}>Positive Vector</div>
                                                                                    <div className="text-[14px] font-bold text-white leading-snug">{step.yes}</div>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* RIGHT LANE: NO FLOW */}
                                                                        <div className="flex flex-col items-center w-fit min-w-[350px]">
                                                                            {Array.isArray(step.no) ? renderWorkflow(step.no, true) : (
                                                                                <div className="w-[300px] bg-[#0f172a]/50 backdrop-blur-md border border-slate-700/50 p-5 rounded-2xl shadow-md z-10 text-center opacity-80 border-dashed">
                                                                                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 mt-1">Negative Vector</div>
                                                                                    <div className="text-[14px] font-bold text-slate-300 leading-snug">{step.no}</div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {/* Vertical line between main steps ONLY if not at end */}
                                                        {idx < steps.length - 1 && step.type === 'decision' && (
                                                            <div className="h-20" />
                                                        )}
                                                    </React.Fragment>
                                                ));
                                            };
                                            return renderWorkflow(flowSteps);
                                        })()}
                                    </div>

                                    {/* Final Termination */}
                                    <div className="flex flex-col items-center mt-32 mb-32 relative">
                                        <div className="flex items-center gap-2 text-xs font-bold text-text-secondary uppercase tracking-widest">
                                            <div className="h-4 w-4" />
                                            DocSprint Subsystem
                                        </div>
                                        <div className="absolute inset-0 bg-red-500/20 blur-xl rounded-full"></div>
                                        <div className="px-16 py-5 bg-[#0f172a] text-white rounded-full font-black text-[16px] uppercase tracking-[0.5em] z-10 shadow-2xl border border-red-500/30 flex items-center gap-4 relative">
                                            PROCESS TERMINATE
                                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        </div>
                                    </div>

                                    <div className="mt-40 pt-10 border-t border-white/5 text-center w-full max-w-[500px]">
                                        <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.6em]">
                                            DocSprintAI Subsystem
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="p-6 border-t border-white/10 bg-black/40 backdrop-blur-xl flex justify-end gap-4 sticky bottom-0 z-10">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsFlowView(false)}
                                    className="rounded-xl border-white/10 font-black tracking-widest text-[10px] uppercase text-white hover:bg-white/10 px-6 transition-colors"
                                >
                                    Close Usage
                                </Button>

                                <Button
                                    onClick={() => triggerDownload(handleDownloadFlow)}
                                    className="rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_20px_rgba(79,70,229,0.3)] px-6 font-black tracking-widest text-[10px] uppercase flex items-center gap-3 transition-colors"
                                >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                    Export Blueprint
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <DownloadConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false);
                    setPendingDownload(null);
                }}
                onConfirm={handleConfirmDownload}
            />


        </div>

    )
}
